import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Search,
  BookOpen,
  HelpCircle,
  LayoutDashboard,
  Package,
  Receipt,
  ClipboardCheck,
  Settings,
  BookText,
  Calculator,
  ChevronRight,
  Image,
  ArrowRight,
  TrendingDown,
  Plus,
  RefreshCw,
  Lightbulb,
  Info,
  FileText,
  Mail,
  MessageCircle,
  ExternalLink,
  CheckCircle2,
  Circle,
  AlertCircle,
  Home,
  TrendingUp,
  ArrowDownCircle,
  PlusCircle,
  Shield,
  BarChart3,
  PieChart,
  MapPin,
  Activity,
  Clock,
  Calendar,
  ShieldCheck,
  MousePointerClick,
  Hand,
  Star,
  ArrowRightLeft,
  XCircle,
  Wrench,
  List,
  Palette,
  DollarSign,
  LayoutGrid,
  ArrowUpDown,
  Eye,
  Grid3x3,
  CloudDownload,
  Download,
  History,
  Filter,
  Columns,
  Printer,
  Trash2,
  Upload,
  Zap,
  Save,
  Play,
  X,
  LayoutList,
  FolderTree,
  UserPlus,
  Repeat,
  AlertTriangle,
  CheckCircle,
  Users,
  Edit,
  Building2,
  MapPinned,
  Database,
  Tag,
  Settings2,
  Plug2,
  RotateCcw,
  FileEdit,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';



interface DocSection {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  color: string;
  subSections?: DocSubSection[];
}

interface DocSubSection {
  id: string;
  title: string;
  parentId: string;
}

interface StepGuide {
  step: number;
  title: string;
  description: string;
  hasScreenshot?: boolean;
}

interface GlossaryTerm {
  term: string;
  definition: string;
  category: string;
}

interface CalculationMethod {
  id: string;
  name: string;
  formula: string;
  description: string;
  example: {
    assetCost: number;
    salvageValue: number;
    usefulLife: number;
    calculations: string[];
  };
}

const docSections: DocSection[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Understanding your dashboard widgets and KPIs',
    color: 'purple',
  },
  {
    id: 'assets',
    title: 'Assets',
    icon: Package,
    description: 'Managing your asset register, creating and editing assets',
    color: 'teal',
  },
  {
    id: 'transactions',
    title: 'Transactions',
    icon: Receipt,
    description: 'Depreciation, additions, revaluations, and disposals',
    color: 'orange',
    subSections: [
      { id: 'depreciation', title: 'Depreciation', parentId: 'transactions' },
      { id: 'additions', title: 'Additions', parentId: 'transactions' },
      { id: 'revaluation', title: 'Revaluation', parentId: 'transactions' },
    ],
  },
  {
    id: 'audits',
    title: 'Audits',
    icon: ClipboardCheck,
    description: 'Physical asset verification and audit management',
    color: 'green',
    subSections: [
      { id: 'create-audit', title: 'Create Audit', parentId: 'audits' },
      { id: 'active-audits', title: 'Active Audits', parentId: 'audits' },
      { id: 'scheduled-audits', title: 'Scheduled Audits', parentId: 'audits' },
      { id: 'audit-history', title: 'Audit History', parentId: 'audits' },
      { id: 'audit-exceptions', title: 'Exceptions', parentId: 'audits' },
    ],
  },
  {
    id: 'administration',
    title: 'Administration',
    icon: Users,
    description: 'Manage locations, categories, equipment, and imports',
    color: 'purple',
    subSections: [
      { id: 'locations', title: 'Locations', parentId: 'administration' },
      { id: 'categories', title: 'Categories', parentId: 'administration' },
      { id: 'equipment', title: 'Equipment', parentId: 'administration' },
      { id: 'disposal-reasons', title: 'Disposal Reasons', parentId: 'administration' },
      { id: 'imports', title: 'Imports', parentId: 'administration' },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: Settings,
    description: 'Configure system features, asset options, integrations, and user permissions',
    color: 'indigo',
    subSections: [
      { id: 'features', title: 'Features', parentId: 'settings' },
      { id: 'asset-options', title: 'Asset Options', parentId: 'settings' },
      { id: 'integration-log', title: 'Integration Log', parentId: 'settings' },
      { id: 'period-management', title: 'Period Management', parentId: 'settings' },
      { id: 'teams-permissions', title: 'Teams & Permissions', parentId: 'settings' },
    ],
  },
  {
    id: 'glossary',
    title: 'Glossary',
    icon: BookText,
    description: 'Key terms and definitions used throughout the application',
    color: 'pink',
  },
  {
    id: 'calculations',
    title: 'Calculations',
    icon: Calculator,
    description: 'Depreciation methods and calculation formulas explained',
    color: 'red',
  },
];

const glossaryTerms: GlossaryTerm[] = [
  // Financial Terms
  { term: 'Acquisition Cost', definition: 'The original purchase price of an asset, including all costs necessary to bring the asset to its intended use (e.g., delivery, installation, taxes).', category: 'Financial' },
  { term: 'Accumulated Depreciation', definition: 'The total amount of depreciation expense that has been recorded against an asset since it was acquired.', category: 'Financial' },
  { term: 'Costing Center', definition: 'A department or division within an organisation used to allocate and track asset costs for financial reporting and budgeting purposes.', category: 'Financial' },
  { term: 'Depreciation', definition: "The systematic allocation of an asset's cost over its useful life to reflect its declining value.", category: 'Financial' },
  { term: 'Depreciation Method', definition: 'The approach used to calculate depreciation (e.g., Straight-Line, Declining Balance, Sum of Years Digits, Units of Production).', category: 'Financial' },
  { term: 'Net Book Value (NBV)', definition: 'The current value of an asset calculated as Acquisition Cost minus Accumulated Depreciation.', category: 'Financial' },
  { term: 'Nominal Account', definition: 'A general ledger account used to record financial transactions related to assets.', category: 'Financial' },
  { term: 'Nominal Code', definition: 'A unique identifier for a nominal account in the general ledger system, used for financial integration and reporting.', category: 'Financial' },
  { term: 'Residual Value', definition: 'The estimated value of an asset at the end of its useful life, also known as salvage value.', category: 'Financial' },
  { term: 'Useful Life', definition: 'The estimated period over which an asset is expected to be usable by the organisation.', category: 'Financial' },
  
  // General Terms
  { term: 'Asset Code', definition: 'A unique identifier assigned to each asset for tracking and reference purposes.', category: 'General' },
  { term: 'Asset Options', definition: 'System-wide configuration settings that control asset management behavior, including transaction settings, period structure, and financial integrations.', category: 'General' },
  { term: 'Asset Register', definition: 'A comprehensive list of all fixed assets owned by an organisation, including details such as cost, location, and depreciation.', category: 'General' },
  { term: 'Category', definition: 'A classification grouping for assets based on their type or nature (e.g., Computer Equipment, Furniture, Vehicles). Categories can be hierarchical with parent-child relationships.', category: 'General' },
  { term: 'Condition', definition: 'The physical state of an asset, typically classified as Excellent, Good, Fair, or Poor. Used during audits to assess asset health.', category: 'General' },
  { term: 'Location', definition: 'The physical or logical place where an asset is situated. Locations can be hierarchical (e.g., Building > Floor > Room) for better organization.', category: 'General' },
  
  // Tracking Terms
  { term: 'Barcode', definition: 'A machine-readable code assigned to an asset for quick identification during audits and tracking.', category: 'Tracking' },
  { term: 'QR Code', definition: 'A two-dimensional barcode that can store more information than traditional barcodes, used for asset identification and mobile scanning.', category: 'Tracking' },
  { term: 'Tag', definition: 'A physical label or identifier attached to an asset, often containing a barcode or QR code for scanning and tracking purposes.', category: 'Tracking' },
  
  // Transaction Terms
  { term: 'Addition', definition: 'A transaction that adds value to an existing asset, such as upgrades, improvements, or enhancements. Can also refer to adding a new asset to the register.', category: 'Transactions' },
  { term: 'Batch', definition: 'A group of related transactions processed together as a single unit. Batches can be saved as drafts and posted when ready.', category: 'Transactions' },
  { term: 'Disposal', definition: 'The process of removing an asset from the register when it is sold, scrapped, or otherwise retired from use.', category: 'Transactions' },
  { term: 'Draft', definition: 'A transaction status indicating that the transaction has been created but not yet posted to the financial system. Drafts can be edited or deleted.', category: 'Transactions' },
  { term: 'Period', definition: 'A defined time interval (e.g., monthly, quarterly) used for financial reporting and depreciation calculations. Periods can follow various patterns like 4-4-5 or calendar months.', category: 'Transactions' },
  { term: 'Posted', definition: 'A transaction status indicating that the transaction has been finalized and recorded in the financial system. Posted transactions cannot be edited.', category: 'Transactions' },
  { term: 'Revaluation', definition: "The process of adjusting an asset's book value to reflect its current fair market value, typically required for compliance with accounting standards.", category: 'Transactions' },
  
  // Audit Terms
  { term: 'Active Audit', definition: 'An audit that is currently in progress, where team members are actively scanning and verifying assets in the field.', category: 'Audits' },
  { term: 'Audit Exception', definition: 'A discrepancy or issue identified during an audit, such as a missing asset, location mismatch, or condition problem that requires resolution.', category: 'Audits' },
  { term: 'Audit History', definition: 'A complete record of all past audits, including dates, participants, assets audited, and any exceptions found.', category: 'Audits' },
  { term: 'Physical Audit', definition: 'A verification process where assets are physically inspected to confirm their existence, location, and condition.', category: 'Audits' },
  { term: 'Scheduled Audit', definition: 'A planned audit activity scheduled for a future date, which can be one-time or recurring (daily, weekly, monthly, quarterly, annually).', category: 'Audits' },
  
  // System & Configuration Terms
  { term: 'Demo Mode', definition: 'A feature toggle state that allows testing functionality without affecting live data. Changes made in demo mode are not saved permanently.', category: 'System' },
  { term: 'Feature Toggle', definition: 'A system setting that enables or disables specific functionality within the application. Features can be set to Live, Demo, or Disabled modes.', category: 'System' },
  { term: 'Integration', definition: 'A connection between the asset management system and external financial systems (e.g., Access Financials) for automatic data synchronization.', category: 'System' },
  { term: 'Live Mode', definition: 'A feature toggle state where functionality is fully enabled and all changes are saved permanently to the database.', category: 'System' },
  { term: 'Period-End Process', definition: 'The procedure for closing a financial period and advancing to the next period. This process posts all pending transactions and updates period-specific data.', category: 'System' },
  
  // Maintenance Terms
  { term: 'Maintenance', definition: 'Scheduled or unscheduled activities performed to keep assets in good working condition. Can be preventive (planned) or corrective (reactive).', category: 'Maintenance' },
  { term: 'Maintenance Schedule', definition: 'A planned timeline for performing regular maintenance activities on assets to prevent breakdowns and extend useful life.', category: 'Maintenance' },
  { term: 'Service History', definition: 'A complete record of all maintenance activities performed on an asset, including dates, descriptions, costs, and technicians involved.', category: 'Maintenance' },
];

const calculationMethods: CalculationMethod[] = [
  {
    id: 'straight-line',
    name: 'Straight-Line Depreciation',
    formula: '(Cost - Residual Value) / Useful Life',
    description: "The most common method that spreads the cost evenly over the asset's useful life. Each period receives the same depreciation expense.",
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Annual Depreciation = (£10,000 - £1,000) / 5 years',
        'Annual Depreciation = £9,000 / 5',
        'Annual Depreciation = £1,800 per year',
        '',
        'Year 1: NBV = £10,000 - £1,800 = £8,200',
        'Year 2: NBV = £8,200 - £1,800 = £6,400',
        'Year 3: NBV = £6,400 - £1,800 = £4,600',
        'Year 4: NBV = £4,600 - £1,800 = £2,800',
        'Year 5: NBV = £2,800 - £1,800 = £1,000 (Residual Value)',
      ],
    },
  },
  {
    id: 'declining-balance',
    name: 'Declining Balance Depreciation',
    formula: 'Book Value x Depreciation Rate',
    description: 'An accelerated method that applies a fixed percentage to the declining book value each period. Results in higher depreciation in early years.',
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Using 40% declining balance rate (double the straight-line rate of 20%)',
        '',
        'Year 1: £10,000 x 40% = £4,000 depreciation, NBV = £6,000',
        'Year 2: £6,000 x 40% = £2,400 depreciation, NBV = £3,600',
        'Year 3: £3,600 x 40% = £1,440 depreciation, NBV = £2,160',
        'Year 4: £2,160 x 40% = £864 depreciation, NBV = £1,296',
        'Year 5: £1,296 - £1,000 = £296 depreciation, NBV = £1,000 (Residual Value)',
        '',
        'Note: In the final year, depreciation is limited to bring NBV to residual value.',
      ],
    },
  },
  {
    id: 'sum-of-years',
    name: 'Sum of Years Digits',
    formula: '(Remaining Life / Sum of Years) x (Cost - Residual Value)',
    description: 'An accelerated method that uses a fraction based on the remaining useful life. Provides higher depreciation in early years.',
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Sum of Years = 5 + 4 + 3 + 2 + 1 = 15',
        'Depreciable Amount = £10,000 - £1,000 = £9,000',
        '',
        'Year 1: (5/15) x £9,000 = £3,000 depreciation, NBV = £7,000',
        'Year 2: (4/15) x £9,000 = £2,400 depreciation, NBV = £4,600',
        'Year 3: (3/15) x £9,000 = £1,800 depreciation, NBV = £2,800',
        'Year 4: (2/15) x £9,000 = £1,200 depreciation, NBV = £1,600',
        'Year 5: (1/15) x £9,000 = £600 depreciation, NBV = £1,000 (Residual Value)',
      ],
    },
  },
  {
    id: 'units-of-production',
    name: 'Units of Production',
    formula: '(Cost - Residual Value) / Total Units x Units Produced',
    description: 'Depreciation based on actual usage or output. Ideal for assets where wear is directly related to production volume.',
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Assume total estimated production: 100,000 units',
        'Depreciation per unit = (£10,000 - £1,000) / 100,000 = £0.09 per unit',
        '',
        'Year 1: 25,000 units x £0.09 = £2,250 depreciation',
        'Year 2: 22,000 units x £0.09 = £1,980 depreciation',
        'Year 3: 20,000 units x £0.09 = £1,800 depreciation',
        'Year 4: 18,000 units x £0.09 = £1,620 depreciation',
        'Year 5: 15,000 units x £0.09 = £1,350 depreciation',
        '',
        'Total: 100,000 units = £9,000 total depreciation',
      ],
    },
  },
];



function StepByStepGuide({ steps, title  }: Readonly<{ steps: StepGuide[]; title: string  }>) {
  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-lg flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        {title}
      </h4>
      <div className="space-y-6">
        {steps.map((step, index) => (
          <div key={step.step} className="relative pl-8">
            <div className="absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
              {step.step}
            </div>
            {index < steps.length - 1 && (
              <div className="absolute left-[11px] top-6 h-full w-0.5 bg-border" />
            )}
            <div className="space-y-3 pb-4">
              <h5 className="font-medium">{step.title}</h5>
              <p className="text-sm text-muted-foreground">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, color }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-200 dark:border-blue-800',
    purple: 'from-purple-500/10 to-purple-500/5 border-purple-200 dark:border-purple-800',
    teal: 'from-teal-500/10 to-teal-500/5 border-teal-200 dark:border-teal-800',
    orange: 'from-orange-500/10 to-orange-500/5 border-orange-200 dark:border-orange-800',
    green: 'from-green-500/10 to-green-500/5 border-green-200 dark:border-green-800',
    indigo: 'from-indigo-500/10 to-indigo-500/5 border-indigo-200 dark:border-indigo-800',
    pink: 'from-pink-500/10 to-pink-500/5 border-pink-200 dark:border-pink-800',
    red: 'from-red-500/10 to-red-500/5 border-red-200 dark:border-red-800',
  };

  const iconColorClasses: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    teal: 'text-teal-600 dark:text-teal-400',
    orange: 'text-orange-600 dark:text-orange-400',
    green: 'text-green-600 dark:text-green-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    pink: 'text-pink-600 dark:text-pink-400',
    red: 'text-red-600 dark:text-red-400',
  };

  return (
    <Card className={cn('relative overflow-hidden border-2 transition-all hover:shadow-md', colorClasses[color])}>
      <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40', colorClasses[color])} />
      <CardHeader className="relative pb-2">
        <Icon className={cn('h-8 w-8 mb-2', iconColorClasses[color])} />
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="relative">
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function SectionNavItem({ section, 
  isActive, 
  onClick, 
  activeSubSection, 
  onSubSectionClick 
 }: Readonly<{ section: DocSection; 
  isActive: boolean; 
  onClick: () => void;
  activeSubSection?: string;
  onSubSectionClick?: (subSectionId: string) => void;
 }>) {
  const Icon = section.icon;
  const colorClasses: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    teal: 'text-teal-600 dark:text-teal-400',
    orange: 'text-orange-600 dark:text-orange-400',
    green: 'text-green-600 dark:text-green-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    pink: 'text-pink-600 dark:text-pink-400',
    red: 'text-red-600 dark:text-red-400',
  };

  const hasSubSections = section.subSections && section.subSections.length > 0;
  const isExpanded = isActive && hasSubSections;

  return (
    <div>
      <button
        onClick={onClick}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all',
          'hover:bg-muted/50',
          isActive && 'bg-primary/10 border border-primary/20'
        )}
      >
        <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-primary' : colorClasses[section.color])} />
        <div className="flex-1 min-w-0">
          <div className={cn('font-medium text-sm truncate', isActive && 'text-primary')}>{section.title}</div>
        </div>
        {isActive && <ChevronRight className="h-4 w-4 text-primary shrink-0" />}
      </button>
      
      {isExpanded && (
        <div className="ml-8 mt-1 space-y-1">
          {section.subSections!.map((subSection) => (
            <button
              key={subSection.id}
              onClick={() => onSubSectionClick?.(subSection.id)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-all',
                'hover:bg-muted/50',
                activeSubSection === subSection.id && 'bg-primary/5 text-primary font-medium'
              )}
            >
              {subSection.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DashboardContent() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    
    const scrollContainer = document.querySelector('.overflow-y-auto') as HTMLElement;
    
    if (element && scrollContainer) {
      const yOffset = -20;
      const elementTop = element.offsetTop;
      const scrollPosition = elementTop + yOffset;
      
      scrollContainer.scrollTo({ 
        top: scrollPosition, 
        behavior: 'smooth' 
      });
    } else {
      console.error('Element or scroll container not found. Element:', element, 'Container:', scrollContainer);
    }
  };

  return (
    <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-2">Dashboard (Home Screen)</h2>
          <p className="text-muted-foreground">
            The Dashboard is your central command centre, providing a comprehensive real-time overview of your entire asset portfolio. 
            Access Assets features two specialised dashboards: <strong>Financial Dashboard</strong> for financial metrics and depreciation tracking, 
            and <strong>Maintenance Dashboard</strong> for asset health, maintenance schedules, and audits.
          </p>
        </div>

      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle>Two Dashboard Views</AlertTitle>
        <AlertDescription>
          Switch between <strong>Financial</strong> and <strong>Maintenance</strong> tabs at the top of the dashboard to access different sets of widgets and KPIs. 
          Your preference is automatically saved and will be remembered on your next visit.
        </AlertDescription>
      </Alert>

      <Separator className="my-8" />

      {/* ========================================
          FINANCIAL DASHBOARD SECTION
      ======================================== */}
      <div id="financial-dashboard">
        <h3 className="text-2xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-blue-600" />
          Financial Dashboard
        </h3>
        <p className="text-muted-foreground mb-6">
          The Financial Dashboard focusses on financial metrics, asset values, depreciation, and cost analysis. 
          It provides comprehensive insights into the monetary aspects of your asset portfolio.
        </p>



        {/* Financial Dashboard Overview */}
        <div>
          <h4 className="text-lg font-semibold mb-4">Overview</h4>
          <div className="grid gap-3 md:grid-cols-2 mb-6">
            <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                Financial KPIs
              </h5>
              <p className="text-xs text-muted-foreground">
                Track Total Asset Value, Acquisition Cost, Total Depreciation, Average Asset Value, and Assets Under Maintenance
              </p>
            </div>
            <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-600" />
                Value Trends
              </h5>
              <p className="text-xs text-muted-foreground">
                Monitor asset value trends over time with interactive line charts showing monthly changes
              </p>
            </div>
            <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <ArrowDownCircle className="h-4 w-4 text-blue-600" />
                Depreciation Analysis
              </h5>
              <p className="text-xs text-muted-foreground">
                View depreciation breakdown by category and track year-to-date depreciation expenses
              </p>
            </div>
            <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Star className="h-4 w-4 text-blue-600" />
                Top Assets
              </h5>
              <p className="text-xs text-muted-foreground">
                Identify your most valuable assets and monitor warranty status across your portfolio
              </p>
            </div>
          </div>
        </div>

        {/* Financial Dashboard KPIs */}
        <div>
          <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Key Performance Indicators
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="border-2 border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Home className="h-4 w-4 text-blue-600" />
                  Total Asset Value
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  The current net book value of all assets in your register. This is the total acquisition cost minus accumulated depreciation.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Financial Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-200 dark:border-green-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  Acquisition Cost
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Total original purchase cost of all assets. This represents the total capital investment in your asset base before any depreciation.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Financial Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 dark:border-orange-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowDownCircle className="h-4 w-4 text-orange-600" />
                  Total Depreciation
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Cumulative depreciation across all assets. Shows the total reduction in asset value due to wear, tear, and obsolescence.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Financial Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 dark:border-purple-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                  Average Asset Value
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Average net book value per asset. Calculated by dividing total asset value by the number of assets. Useful for portfolio analysis.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Financial Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 dark:border-teal-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-teal-600" />
                  Assets Under Maintenance
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Number of assets currently undergoing maintenance or repair. Helps track operational availability and maintenance workload.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Operational Metric</Badge>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Financial Dashboard Widgets */}
        <div className="mt-8">
          <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Charts & Analytics Widgets
          </h4>
          <p className="text-muted-foreground mb-4">
            The Financial Dashboard features interactive charts and visualisations focussed on value trends, depreciation analysis, and financial metrics.
          </p>

          <Alert className="mb-6 border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30">
            <Palette className="h-4 w-4 text-purple-600" />
            <AlertTitle>Customisable Widgets</AlertTitle>
            <AlertDescription>
              Click the "Customise Dashboard" button to show/hide widgets, rearrange their layout, and personalise your dashboard view. 
              Your preferences are saved automatically.
            </AlertDescription>
          </Alert>

          <div className="space-y-6">
            <div>
              <h5 className="font-semibold text-base mb-3">Asset Value Trend</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Line chart showing the total net book value of your assets over the last 12 months, helping you track portfolio value changes.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span><strong>Trend Visualisation:</strong> See how your asset value changes month-over-month</span>
                </div>
                <div className="flex items-start gap-2">
                  <MousePointerClick className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span><strong>Interactive:</strong> Hover over data points to see exact values and dates</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-base mb-3">Depreciation by Category</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Bar chart displaying depreciation expenses broken down by asset category, showing which categories contribute most to depreciation.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <BarChart3 className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                  <span><strong>Category Comparison:</strong> Compare depreciation across different asset types</span>
                </div>
                <div className="flex items-start gap-2">
                  <TrendingDown className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                  <span><strong>Expense Tracking:</strong> Identify which categories have the highest depreciation costs</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-base mb-3">Top Assets by Value</h5>
              <p className="text-sm text-muted-foreground mb-3">
                List of your most valuable assets ranked by net book value, helping you focus on high-value items.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <Star className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                  <span><strong>Value Ranking:</strong> See your top 10 most valuable assets at a glance</span>
                </div>
                <div className="flex items-start gap-2">
                  <MousePointerClick className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                  <span><strong>Quick Access:</strong> Click any asset to view full details</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-base mb-3">Warranty Status</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Overview of warranty coverage across your asset portfolio, showing active, expiring, and expired warranties.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Coverage Status:</strong> Track warranty coverage to plan for renewals or replacements</span>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Expiration Alerts:</strong> See warranties expiring in the next 90 days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-8" />

      {/* ========================================
          MAINTENANCE DASHBOARD SECTION
      ======================================== */}
      <div id="maintenance-dashboard">
        <h3 className="text-2xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <Wrench className="h-6 w-6 text-orange-600" />
          Maintenance Dashboard
        </h3>
        <p className="text-muted-foreground mb-6">
          The Maintenance Dashboard emphasises asset health, maintenance schedules, audits, and operational metrics. 
          It provides comprehensive insights into the operational aspects of your asset portfolio.
        </p>

        {/* Maintenance Dashboard KPIs */}
        <div>
          <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-orange-600" />
            Key Performance Indicators
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="border-2 border-green-200 dark:border-green-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="h-4 w-4 text-green-600" />
                  Total Assets
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Total count of all assets in your register, regardless of status. Provides an overview of your complete asset inventory.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Count Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-blue-600" />
                  Active Assets
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Number of assets currently in active operational use. Indicates the working capacity of your asset portfolio.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Status Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 dark:border-orange-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  Maintenance Due
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Assets with scheduled maintenance due within the next 30 days. Helps prioritise upcoming maintenance activities.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Schedule Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 dark:border-purple-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4 text-purple-600" />
                  Audits Pending
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Assets requiring audit verification within the next 90 days. Tracks compliance with audit schedules.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Compliance Metric</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-200 dark:border-red-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Activity className="h-4 w-4 text-red-600" />
                  Asset Health
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p className="text-muted-foreground">
                  Overall health score of your asset portfolio. Calculated based on maintenance history, age, and condition assessments.
                </p>
                <Badge variant="outline" className="text-xs w-fit">Health Metric</Badge>
              </CardContent>
            </Card>
          </div>

          <Alert className="mt-4">
            <Info className="h-4 w-4" />
            <AlertTitle>Interactive KPI Cards</AlertTitle>
            <AlertDescription>
              Click on any KPI card to drill down into detailed views and reports for that specific metric. 
              Hover over cards to see additional information and trend indicators.
            </AlertDescription>
          </Alert>
        </div>

        {/* Maintenance Dashboard Widgets */}
        <div className="mt-8">
          <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-orange-600" />
            Charts & Analytics Widgets
          </h4>
          <p className="text-muted-foreground mb-4">
            The Maintenance Dashboard features interactive charts and visualisations emphasising asset health, maintenance schedules, and operational metrics.
          </p>

          <Alert className="mb-6 border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30">
            <Palette className="h-4 w-4 text-purple-600" />
            <AlertTitle>Customisable Widgets</AlertTitle>
            <AlertDescription>
              Click the "Customise Dashboard" button to show/hide widgets, rearrange their layout, and personalise your dashboard view. 
              Your preferences are saved automatically.
            </AlertDescription>
          </Alert>

          <div className="space-y-6">
            <div>
              <h5 className="font-semibold text-base mb-3">Asset Health Overview</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Donut chart showing the distribution of assets by health status (Healthy, Needs Attention, Critical).
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <PieChart className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Health Distribution:</strong> Visual breakdown of asset health across your portfolio</span>
                </div>
                <div className="flex items-start gap-2">
                  <Activity className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Health Score:</strong> Overall portfolio health percentage displayed prominently</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-base mb-3">Maintenance & Insurance Calendar</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Interactive calendar showing scheduled maintenance tasks and upcoming insurance renewals for the next 90 days.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span><strong>Schedule View:</strong> See all upcoming maintenance and insurance events in one place</span>
                </div>
                <div className="flex items-start gap-2">
                  <MousePointerClick className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span><strong>Event Details:</strong> Click any event to view details and take action</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-base mb-3">Audit Status</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Progress tracking for asset audits, showing completion status and assets requiring verification.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <ClipboardCheck className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                  <span><strong>Audit Progress:</strong> Visual progress bar showing percentage of assets audited</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                  <span><strong>Due Dates:</strong> List of assets with upcoming or overdue audit requirements</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-base mb-3">Exception Alerts</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Real-time alerts for assets requiring immediate attention due to critical issues, overdue maintenance, or expired coverage.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  <span><strong>Priority Alerts:</strong> Colour-coded by severity (Critical, High, Medium, Low)</span>
                </div>
                <div className="flex items-start gap-2">
                  <List className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  <span><strong>Action Items:</strong> Categorised by exception type for easy prioritisation</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold text-base mb-3">Asset Age Analysis</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Bar chart showing the age distribution of your assets, helping identify aging equipment that may need replacement.
              </p>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <BarChart3 className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                  <span><strong>Age Groups:</strong> Assets grouped by age ranges (0-2 years, 3-5 years, 6-10 years, 10+ years)</span>
                </div>
                <div className="flex items-start gap-2">
                  <TrendingUp className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                  <span><strong>Replacement Planning:</strong> Identify assets approaching end of useful life</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interacting with Dashboard */}
      <div id="interacting">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <MousePointerClick className="h-5 w-5 text-indigo-600" />
          Interacting with the Dashboard
        </h3>
        <p className="text-muted-foreground mb-4">
          Both dashboards are fully interactive, allowing you to drill down into details and take actions directly from the overview.
        </p>

        <div className="grid gap-4 md:grid-cols-2 mb-6">
          <Card className="border-2 border-blue-200 dark:border-blue-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MousePointerClick className="h-4 w-4" />
                Click Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">KPI Cards</Badge>
                <span className="text-muted-foreground">Click to view detailed reports and filtered asset lists</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">Widgets</Badge>
                <span className="text-muted-foreground">Click on chart segments or widget items to drill down into details</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">Quick Actions</Badge>
                <span className="text-muted-foreground">Use quick action buttons to perform common tasks directly from the dashboard</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-200 dark:border-purple-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Hand className="h-4 w-4" />
                Hover Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">Chart Points</Badge>
                <span className="text-muted-foreground">Hover over chart data points to see exact values and dates</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">KPI Trends</Badge>
                <span className="text-muted-foreground">Hover to see detailed trend information and percentage changes</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">Tooltips</Badge>
                <span className="text-muted-foreground">Hover over icons and labels for helpful tooltips and explanations</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
          <Lightbulb className="h-4 w-4 text-green-600" />
          <AlertTitle>Pro Tip: Quick Navigation</AlertTitle>
          <AlertDescription>
            Use the Dashboard as your navigation hub. Click on KPI cards and widget items to quickly jump to filtered views of your assets, saving time and clicks.
          </AlertDescription>
        </Alert>
      </div>

      <Separator className="my-8" />

      {/* Data Refresh */}
      <div id="data-refresh">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-600" />
          Data Refresh & Updates
        </h3>
        <p className="text-muted-foreground mb-4">
          Understanding how and when Dashboard data is updated helps you work with the most current information.
        </p>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Automatic Refresh</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>The Dashboard automatically refreshes data in the following scenarios:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li>When you first navigate to the Dashboard page</li>
                <li>After creating, updating, or deleting an asset</li>
                <li>After recording a new transaction</li>
                <li>When switching between Financial and Maintenance dashboards</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Manual Refresh</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p className="mb-3">If you need to manually refresh the Dashboard data, use the refresh button in the dashboard header.</p>
            </CardContent>
          </Card>

          <Alert>
            <Clock className="h-4 w-4" />
            <AlertTitle>Real-Time Updates</AlertTitle>
            <AlertDescription>
              In multi-user environments, the Dashboard will show a notification banner when new data is available, 
              prompting you to refresh to see the latest changes made by other users.
            </AlertDescription>
          </Alert>
        </div>
      </div>

      <Separator className="my-8" />

      {/* Related Topics */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Related Help Topics</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Package className="h-4 w-4 text-teal-600" />
                Managing Assets
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Learn how to create, edit, and manage assets in your register
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-blue-600" />
                Recording Transactions
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Understand how to record acquisitions, disposals, and transfers
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calculator className="h-4 w-4 text-purple-600" />
                Depreciation Methods
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Explore different depreciation calculation methods and formulas
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4 text-orange-600" />
                Generating Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Create and export detailed reports from your asset data
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Table of Contents */}
      <div className="hidden xl:block fixed right-8 top-64 w-56 z-10">
        <Card className="shadow-lg border-border/50">
          <CardHeader className="pb-3 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <List className="h-4 w-4 text-primary" />
              On This Page
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <nav className="space-y-1">
              <a
                href="#financial-dashboard"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('financial-dashboard');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Financial Dashboard
                </span>
              </a>
              <a
                href="#maintenance-dashboard"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('maintenance-dashboard');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Maintenance Dashboard
                </span>
              </a>
              <a
                href="#interacting"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('interacting');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Interacting
                </span>
              </a>
              <a
                href="#data-refresh"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('data-refresh');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Data Refresh
                </span>
              </a>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AssetsContent() {
  const createAssetSteps: StepGuide[] = [
    { step: 1, title: 'Navigate to Assets', description: "Click 'Assets' in the sidebar to open the asset register.", hasScreenshot: true },
    { step: 2, title: 'Click New Asset', description: "Click the 'New Asset' button in the top right corner of the page.", hasScreenshot: true },
    { step: 3, title: 'Enter Basic Details', description: 'Fill in the Asset Code, Name, Description, Category, and Location fields.', hasScreenshot: true },
    { step: 4, title: 'Set Financial Values', description: 'Enter the Acquisition Cost, Acquisition Date, and configure depreciation settings.', hasScreenshot: true },
    { step: 5, title: 'Add Additional Information', description: 'Optionally add serial numbers, barcodes, supplier details, and attach documents or photos.', hasScreenshot: true },
    { step: 6, title: 'Save the Asset', description: "Click 'Save' to create the asset in your register.", hasScreenshot: true },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    const scrollContainer = document.querySelector('.overflow-y-auto') as HTMLElement;
    
    if (element && scrollContainer) {
      const yOffset = -20;
      const elementTop = element.offsetTop;
      const scrollPosition = elementTop + yOffset;
      scrollContainer.scrollTo({ 
        top: scrollPosition, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Assets</h2>
        <p className="text-muted-foreground">
          The Assets module is the heart of Access Assets, where you manage your complete asset register. This comprehensive guide covers all features available on the Assets page.
        </p>
      </div>

      {/* Overview Card */}
      <div id="assets-overview">
      <Card className="relative overflow-hidden border-2 border-teal-200 dark:border-teal-800">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-teal-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-teal-600" />
            Asset Register Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            The asset register displays all your fixed assets in either a gallery view (with images) or a table view (detailed list). You can filter, sort, search, and perform bulk actions to manage your assets efficiently.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <LayoutGrid className="h-4 w-4 text-teal-600" />
                Gallery View
              </h4>
              <p className="text-xs text-muted-foreground mt-1">Visual card-based view showing asset images, ideal for quick identification. Adjustable grid size (4, 5, or 6 columns).</p>
            </div>
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <List className="h-4 w-4 text-teal-600" />
                Table View
              </h4>
              <p className="text-xs text-muted-foreground mt-1">Detailed list view with customisable columns, resizable widths, and sortable headers for comprehensive data analysis.</p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      <Separator className="my-8" />

      {/* Page Header Controls */}
      <div id="page-header">
        <h3 className="text-xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <MousePointerClick className="h-5 w-5 text-teal-600" />
          Page Header Controls
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          The Assets page header provides powerful tools for searching, sorting, and viewing your assets.
        </p>

        <div className="grid gap-4">
          {/* Search Bar */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="h-4 w-4 text-teal-600" />
                Search Bar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Quickly find assets by typing in the search field. The search looks through:
              </p>
              <ul className="space-y-1 text-sm ml-4">
                <li className="flex items-start gap-2">
                  <Circle className="h-3 w-3 text-muted-foreground mt-1.5 shrink-0" />
                  <span>Asset Code</span>
                </li>
                <li className="flex items-start gap-2">
                  <Circle className="h-3 w-3 text-muted-foreground mt-1.5 shrink-0" />
                  <span>Asset Name</span>
                </li>
                <li className="flex items-start gap-2">
                  <Circle className="h-3 w-3 text-muted-foreground mt-1.5 shrink-0" />
                  <span>Serial Number</span>
                </li>
                <li className="flex items-start gap-2">
                  <Circle className="h-3 w-3 text-muted-foreground mt-1.5 shrink-0" />
                  <span>Description</span>
                </li>
              </ul>
              <Badge variant="outline" className="text-xs">Real-time filtering</Badge>
            </CardContent>
          </Card>

          {/* Sort Dropdown */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-teal-600" />
                Sort Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Sort your assets using the dropdown menu. Available sort options:
              </p>
              <div className="grid gap-2 md:grid-cols-2">
                <div className="rounded-lg border p-2 text-sm">
                  <strong>Category:</strong> A-Z or Z-A
                </div>
                <div className="rounded-lg border p-2 text-sm">
                  <strong>Name:</strong> A-Z or Z-A
                </div>
                <div className="rounded-lg border p-2 text-sm">
                  <strong>Date:</strong> Newest or Oldest
                </div>
                <div className="rounded-lg border p-2 text-sm">
                  <strong>Value:</strong> High-Low or Low-High
                </div>
              </div>
              <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-xs">
                  Default sorting is by Category (A-Z). Your sort preference is remembered during your session.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* View Mode Toggle */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4 text-teal-600" />
                View Mode Toggle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Switch between Gallery and List views using the toggle buttons.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm flex items-center gap-2">
                    <List className="h-4 w-4" />
                    List View
                  </h5>
                  <p className="text-xs text-muted-foreground mt-1">
                    Displays assets in a table format with customisable columns. Best for detailed data analysis and bulk operations.
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4" />
                    Gallery View
                  </h5>
                  <p className="text-xs text-muted-foreground mt-1">
                    Shows assets as visual cards with images. Best for quick identification and browsing.
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="text-xs">Preference saved automatically</Badge>
            </CardContent>
          </Card>

          {/* Grid Size Selector */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Grid3x3 className="h-4 w-4 text-teal-600" />
                Grid Size Selector (Gallery View Only)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                When in Gallery view, adjust the number of columns displayed:
              </p>
              <div className="flex gap-2">
                <Badge variant="secondary">4 Columns</Badge>
                <Badge variant="secondary">5 Columns (Default)</Badge>
                <Badge variant="secondary">6 Columns</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Choose based on your screen size and preference. More columns show more assets at once but with smaller cards.
              </p>
            </CardContent>
          </Card>

          {/* New Asset Button */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="h-4 w-4 text-teal-600" />
                New Asset Button
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Click the "New Asset" button in the top right to create a new asset. This opens the asset creation form where you can enter all asset details.
              </p>
            </CardContent>
          </Card>

          {/* Import from Integration */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CloudDownload className="h-4 w-4 text-teal-600" />
                Import from Integration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                If you have an active integration with Access Financials, this button appears and allows you to import assets directly from your accounting system.
              </p>
              <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-xs">
                  Only visible when Access Financials integration is configured and enabled in Settings.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator className="my-8" />

      {/* Right Panel Features */}
      <div id="right-panel">
        <h3 className="text-xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <Palette className="h-5 w-5 text-teal-600" />
          Right Panel Features
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          The right panel provides three powerful tabs for managing your assets: Actions, Filters, and Columns.
        </p>

        <div className="space-y-6">
          {/* Actions Tab */}
          <div>
            <h4 className="text-base font-semibold mb-4 flex items-center gap-2">
              <Zap className="h-4 w-4 text-teal-600" />
              Actions Tab
            </h4>
            <div>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  The Actions tab provides quick access to common operations. Actions are organized into sections:
                </p>

                {/* Export Section */}
                <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
                  <h5 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Download className="h-4 w-4 text-blue-600" />
                    Export (Always Available)
                  </h5>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <strong>Export to CSV:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Download all visible assets as a CSV file. Includes only the columns currently visible in your view.</p>
                    </div>
                    <div className="text-sm">
                      <strong>Export to Excel:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Generate an Excel spreadsheet with all visible asset data. Better formatting and easier to work with in Excel.</p>
                    </div>
                  </div>
                </div>

                {/* View History Section */}
                <div className="rounded-lg border p-4 bg-purple-50/50 dark:bg-purple-950/20">
                  <h5 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <History className="h-4 w-4 text-purple-600" />
                    View History (Requires Selection)
                  </h5>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <strong>Transaction History:</strong>
                      <p className="text-xs text-muted-foreground mt-1">View all financial transactions (depreciation, additions, revaluations, disposals) for selected assets.</p>
                    </div>
                    <div className="text-sm">
                      <strong>View Audit:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Open audit records and compliance data for selected assets. Shows audit trail and verification history.</p>
                    </div>
                  </div>
                </div>

                {/* Create Transactions Section */}
                <div className="rounded-lg border p-4 bg-green-50/50 dark:bg-green-950/20">
                  <h5 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <PlusCircle className="h-4 w-4 text-green-600" />
                    Create Transactions (Requires Selection)
                  </h5>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <strong>Addition:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Record new asset additions or add value to existing assets (upgrades, improvements).</p>
                    </div>
                    <div className="text-sm">
                      <strong>Depreciation:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Calculate and apply depreciation for selected assets. Creates a depreciation batch.</p>
                    </div>
                    <div className="text-sm">
                      <strong>Revaluation:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Adjust asset values to reflect current market conditions. Can increase or decrease values.</p>
                    </div>
                    <div className="text-sm">
                      <strong>Disposal:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Process asset disposal transaction. Only available when exactly one asset is selected.</p>
                    </div>
                  </div>
                </div>

                {/* Other Actions Section */}
                <div className="rounded-lg border p-4 bg-orange-50/50 dark:bg-orange-950/20">
                  <h5 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-orange-600" />
                    Other Actions (Requires Selection)
                  </h5>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <strong>Print Asset Labels:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Generate barcode or QR code labels for selected assets. Useful for physical asset tagging.</p>
                    </div>
                    <div className="text-sm">
                      <strong>Delete Assets:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Permanently remove selected assets from the register. This action cannot be undone.</p>
                    </div>
                  </div>
                </div>

                <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-xs">
                    Select one or more assets using the checkboxes to unlock additional actions. The panel automatically expands when you make a selection.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </div>

          {/* Filters Tab */}
          <div>
            <h4 className="text-base font-semibold mb-4 flex items-center gap-2">
              <Filter className="h-4 w-4 text-teal-600" />
              Filters Tab
            </h4>
            <div>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  The Filters tab provides comprehensive filtering options to narrow down your asset list. Filters are applied in real-time as you make selections.
                </p>

                <div className="space-y-3">
                  {/* Status Filter */}
                  <div className="rounded-lg border p-3">
                    <h5 className="font-medium text-sm mb-2">Status Filter</h5>
                    <p className="text-xs text-muted-foreground mb-2">Filter assets by their current status:</p>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Pending</Badge>
                      <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">Inactive</Badge>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Maintenance</Badge>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Disposed</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      <strong>Default:</strong> Active status is selected by default when you first visit the page.
                    </p>
                  </div>

                  {/* Category Filter */}
                  <div className="rounded-lg border p-3">
                    <h5 className="font-medium text-sm mb-2">Category Filter</h5>
                    <p className="text-xs text-muted-foreground">
                      Filter by asset categories (e.g., Computer Equipment, Furniture, Vehicles). Multiple categories can be selected.
                    </p>
                  </div>

                  {/* Location Filter */}
                  <div className="rounded-lg border p-3">
                    <h5 className="font-medium text-sm mb-2">Location Filter</h5>
                    <p className="text-xs text-muted-foreground">
                      Filter by asset locations. Select one or more locations to show only assets at those sites.
                    </p>
                  </div>

                  {/* Date Range Filters */}
                  <div className="rounded-lg border p-3">
                    <h5 className="font-medium text-sm mb-2">Date Range Filters</h5>
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <p><strong>Acquisition Date Range:</strong> Filter assets purchased within a specific date range.</p>
                      <p><strong>Warranty Expiry Date Range:</strong> Find assets with warranties expiring in a specific period.</p>
                    </div>
                  </div>

                  {/* Value Range Filters */}
                  <div className="rounded-lg border p-3">
                    <h5 className="font-medium text-sm mb-2">Value Range Filters</h5>
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <p><strong>Acquisition Cost Range:</strong> Filter by minimum and/or maximum acquisition cost.</p>
                      <p><strong>Net Book Value Range:</strong> Filter by current asset value range.</p>
                    </div>
                  </div>

                  {/* Additional Filters */}
                  <div className="rounded-lg border p-3">
                    <h5 className="font-medium text-sm mb-2">Additional Filters</h5>
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <p><strong>Assigned To:</strong> Filter by person or department the asset is assigned to.</p>
                      <p><strong>Department:</strong> Filter by cost centre or department.</p>
                      <p><strong>Manufacturer:</strong> Filter by asset manufacturer.</p>
                      <p><strong>Depreciation Method:</strong> Filter by depreciation calculation method.</p>
                    </div>
                  </div>
                </div>

                <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-xs">
                    <strong>Active Filter Count:</strong> The Filters tab shows a badge with the number of active filters. Click "Clear All" to reset filters to defaults.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </div>

          {/* Columns Tab */}
          <div>
            <h4 className="text-base font-semibold mb-4 flex items-center gap-2">
              <Columns className="h-4 w-4 text-teal-600" />
              Columns Tab
            </h4>
            <div>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  The Columns tab allows you to customize which columns are visible in the table view and their order. This helps you focus on the data that matters most to you.
                </p>

                <div className="space-y-3">
                  {/* Show/Hide Columns */}
                  <div className="rounded-lg border p-3 bg-blue-50/50 dark:bg-blue-950/20">
                    <h5 className="font-medium text-sm mb-2">Show/Hide Columns</h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      Toggle checkboxes to show or hide specific columns. Available columns include:
                    </p>
                    <div className="grid grid-cols-2 gap-1.5 text-xs">
                      <div>• Asset Code</div>
                      <div>• Asset Name</div>
                      <div>• Description</div>
                      <div>• Category</div>
                      <div>• Location</div>
                      <div>• Status</div>
                      <div>• Serial Number</div>
                      <div>• Barcode</div>
                      <div>• Acquisition Cost</div>
                      <div>• Net Book Value</div>
                      <div>• Accumulated Depreciation</div>
                      <div>• Acquisition Date</div>
                      <div>• Assigned To</div>
                      <div>• Department</div>
                      <div>• Manufacturer</div>
                      <div>• Model</div>
                      <div>• Warranty Expiry</div>
                      <div>• And more...</div>
                    </div>
                  </div>

                  {/* Reorder Columns */}
                  <div className="rounded-lg border p-3 bg-purple-50/50 dark:bg-purple-950/20">
                    <h5 className="font-medium text-sm mb-2">Reorder Columns</h5>
                    <p className="text-xs text-muted-foreground">
                      Drag and drop columns in the Columns tab to change their order in the table. Your preferred order is saved automatically.
                    </p>
                  </div>

                  {/* Resize Columns */}
                  <div className="rounded-lg border p-3 bg-green-50/50 dark:bg-green-950/20">
                    <h5 className="font-medium text-sm mb-2">Resize Columns (Table View)</h5>
                    <p className="text-xs text-muted-foreground">
                      In table view, hover over the column divider in the header and drag to resize. Column widths are remembered during your session.
                    </p>
                  </div>

                  {/* Default Columns */}
                  <div className="rounded-lg border p-3">
                    <h5 className="font-medium text-sm mb-2">Default Visible Columns</h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      By default, these columns are visible:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="secondary" className="text-xs">Code</Badge>
                      <Badge variant="secondary" className="text-xs">Name</Badge>
                      <Badge variant="secondary" className="text-xs">Category</Badge>
                      <Badge variant="secondary" className="text-xs">Location</Badge>
                      <Badge variant="secondary" className="text-xs">Status</Badge>
                      <Badge variant="secondary" className="text-xs">Acquisition Cost</Badge>
                      <Badge variant="secondary" className="text-xs">Net Book Value</Badge>
                    </div>
                  </div>
                </div>

                <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-xs">
                    Column customization only applies to Table view. Gallery view always shows the same card layout.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-8" />

      {/* Asset Selection and Bulk Operations */}
      <div id="asset-selection">
        <h3 className="text-xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <Hand className="h-5 w-5 text-teal-600" />
          Asset Selection & Bulk Operations
        </h3>
        
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Selecting Assets</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Select individual assets or multiple assets to perform bulk operations:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span><strong>Individual Selection:</strong> Click the checkbox next to any asset to select it.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span><strong>Select All:</strong> Use the checkbox in the table header (or above the gallery) to select all assets on the current page.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span><strong>Selection Counter:</strong> The selection count is displayed above the asset list (e.g., "3 items selected").</span>
              </li>
            </ul>
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-xs">
                When you select assets, the right panel automatically opens to the Actions tab, showing available bulk operations.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Available Bulk Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm flex items-center gap-2">
                  <Download className="h-4 w-4 text-blue-600" />
                  Export
                </h5>
                <p className="text-xs text-muted-foreground mt-1">Export selected assets to CSV or Excel format.</p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm flex items-center gap-2">
                  <Printer className="h-4 w-4 text-purple-600" />
                  Print Labels
                </h5>
                <p className="text-xs text-muted-foreground mt-1">Generate barcode or QR code labels for physical tagging.</p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-orange-600" />
                  Depreciation
                </h5>
                <p className="text-xs text-muted-foreground mt-1">Create a depreciation batch for selected assets.</p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-red-600" />
                  Delete
                </h5>
                <p className="text-xs text-muted-foreground mt-1">Permanently remove selected assets from the register.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator className="my-8" />

      {/* Pagination */}
      <div id="pagination">
        <h3 className="text-xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5 text-teal-600" />
          Pagination
        </h3>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Navigating Large Asset Lists</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The Assets page displays 100 assets per page. Use the pagination controls at the bottom of the page to navigate:
            </p>
            <div className="grid gap-3">
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm">Page Navigation</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Use First, Previous, Next, and Last buttons to jump between pages. Click specific page numbers to go directly to that page.
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm">Entry Counter</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  The footer shows "Showing 1-100 of 500 entries" to indicate your current position in the full list.
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm">Total Net Book Value</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  The footer also displays the total Net Book Value of all filtered assets (not just the current page).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator className="my-8" />

      {/* Creating Assets */}
      <div id="creating-assets">
      <StepByStepGuide steps={createAssetSteps} title="Creating a New Asset" />
      </div>

      <Separator className="my-8" />

      {/* Additional Features */}
      <div id="additional-features">
        <h3 className="text-xl font-bold tracking-tight mb-4">Additional Features</h3>
        <div className="space-y-6">
          <div>
            <h4 className="text-base font-semibold mb-4">Asset Detail Tabs</h4>
            <div>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                When you click on an asset, you'll see detailed information organized into tabs:
              </p>
              <div className="space-y-3">
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Details Tab</h5>
                  <p className="text-xs text-muted-foreground">Basic information including code, name, description, category, location, and status.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Values Tab</h5>
                  <p className="text-xs text-muted-foreground">Financial information including acquisition cost, net book value, accumulated depreciation, and current value.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Settings Tab</h5>
                  <p className="text-xs text-muted-foreground">Depreciation method, rate, useful life, residual value, and nominal account mappings.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Supplier Tab</h5>
                  <p className="text-xs text-muted-foreground">Supplier details, purchase order information, warranty details, and insurance information.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Documents & Photos Tab</h5>
                  <p className="text-xs text-muted-foreground">Attached files, images, receipts, manuals, and complete audit trail history.</p>
                </div>
              </div>
            </div>
            </div>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-4">Importing Assets</h4>
            <div>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Import assets in bulk using CSV files or sync from your accounting system:
              </p>
              
              <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
                <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <CloudDownload className="h-4 w-4 text-blue-600" />
                  Integration Import
                </h5>
                <p className="text-xs text-muted-foreground">
                  If you have Access Financials integration enabled, click "Import from Integration" to sync assets from your accounting system. 
                  The system will check for duplicates and only import new assets.
                </p>
              </div>

              <StepByStepGuide
                title="CSV Import Process"
                steps={[
                  { step: 1, title: 'Prepare Your CSV', description: 'Download the template and populate it with your asset data. Ensure all required fields are filled.', hasScreenshot: true },
                  { step: 2, title: 'Upload File', description: 'Navigate to Assets > Import and upload your CSV file.', hasScreenshot: true },
                  { step: 3, title: 'Map Columns', description: 'Match your CSV columns to the corresponding asset fields in the system.', hasScreenshot: true },
                  { step: 4, title: 'Review & Import', description: 'Review the preview of assets to be imported and click Import to create them.', hasScreenshot: true },
                ]}
              />
            </div>
            </div>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-4">Special Filter: Assets Requiring Attention</h4>
            <div>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                The "Assets Requiring Attention" filter is a special pre-configured filter that shows assets needing immediate action:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span>Assets with warranty expiring within 30 days</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span>Assets with insurance expiring within 30 days</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span>Assets with custom date fields expiring within 30 days</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span>Assets with "Disposed" status</span>
                </li>
              </ul>
              <p className="text-xs text-muted-foreground">
                This filter is typically accessed from the Dashboard&apos;s &quot;Assets Requiring Attention&quot; widget.
              </p>
            </div>
            </div>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-4">Tips & Best Practices</h4>
            <div>
            <div className="space-y-4 pt-2">
              <div className="grid gap-3">
                <div className="rounded-lg border p-3 bg-green-50/50 dark:bg-green-950/20">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <div>
                      <h5 className="font-medium text-sm">Use Filters Effectively</h5>
                      <p className="text-xs text-muted-foreground mt-1">
                        Combine multiple filters to create precise asset lists. For example, filter by Category + Location + Status to find specific groups of assets.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border p-3 bg-blue-50/50 dark:bg-blue-950/20">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <h5 className="font-medium text-sm">Customize Your View</h5>
                      <p className="text-xs text-muted-foreground mt-1">
                        Set up your preferred columns and view mode once. The system remembers your preferences for future visits.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border p-3 bg-purple-50/50 dark:bg-purple-950/20">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                    <div>
                      <h5 className="font-medium text-sm">Export Before Bulk Changes</h5>
                      <p className="text-xs text-muted-foreground mt-1">
                        Before performing bulk deletions or major changes, export your asset list as a backup.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border p-3 bg-amber-50/50 dark:bg-amber-950/20">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <h5 className="font-medium text-sm">Regular Audits</h5>
                      <p className="text-xs text-muted-foreground mt-1">
                        Use the asset register to plan and track physical audits. Filter by location to create location-specific audit lists.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Table of Contents */}
      <div className="hidden xl:block fixed right-8 top-64 w-56 z-10">
        <Card className="shadow-lg border-border/50">
          <CardHeader className="pb-3 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <List className="h-4 w-4 text-primary" />
              On This Page
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <nav className="space-y-1">
              <a
                href="#assets-overview"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('assets-overview');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Overview
                </span>
              </a>
              <a
                href="#page-header"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('page-header');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Page Header
                </span>
              </a>
              <a
                href="#right-panel"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('right-panel');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Right Panel
                </span>
              </a>
              <a
                href="#asset-selection"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('asset-selection');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Asset Selection
                </span>
              </a>
              <a
                href="#pagination"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('pagination');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Pagination
                </span>
              </a>
              <a
                href="#creating-assets"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('creating-assets');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Creating Assets
                </span>
              </a>
              <a
                href="#additional-features"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('additional-features');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Additional Features
                </span>
              </a>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TransactionsContent({ onNavigateToSubSection  }: Readonly<{ onNavigateToSubSection: (subSectionId: string) => void  }>) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Transactions Overview</h2>
        <p className="text-muted-foreground">
          The Transactions module is your central hub for processing all financial activities related to your assets. 
          Access Assets provides a comprehensive, controlled workflow for depreciation, additions, and revaluations, 
          ensuring accuracy, auditability, and compliance with accounting standards.
        </p>
      </div>

      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle>Transaction Processing Overview</AlertTitle>
        <AlertDescription>
          All transaction types follow a <strong>batch-based workflow</strong> with built-in validation, review, and approval stages. 
          This ensures data integrity and provides a complete audit trail for all financial activities.
        </AlertDescription>
      </Alert>

      <Separator className="my-8" />

      {/* Transaction Types Overview */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Transaction Types</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Click on any transaction type below to view detailed documentation, workflows, and best practices.
        </p>
        <div className="grid gap-4 md:grid-cols-3">
          <Card 
            className="border-2 border-orange-200 dark:border-orange-800 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
            onClick={() => onNavigateToSubSection('depreciation')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-orange-600" />
                Depreciation
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <p className="text-muted-foreground">
                Calculate and post periodic depreciation expenses. Supports multiple depreciation methods and automated batch processing.
              </p>
              <Badge variant="outline" className="text-xs w-fit">Periodic Transaction</Badge>
              <div className="flex items-center gap-1 text-xs text-orange-600 font-medium pt-2">
                <span>View detailed guide</span>
                <ArrowRight className="h-3 w-3" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-2 border-green-200 dark:border-green-800 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
            onClick={() => onNavigateToSubSection('additions')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-600" />
                Additions
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <p className="text-muted-foreground">
                Record new asset acquisitions or add value to existing assets through upgrades, improvements, or enhancements.
              </p>
              <Badge variant="outline" className="text-xs w-fit">Ad-hoc Transaction</Badge>
              <div className="flex items-center gap-1 text-xs text-green-600 font-medium pt-2">
                <span>View detailed guide</span>
                <ArrowRight className="h-3 w-3" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-2 border-blue-200 dark:border-blue-800 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
            onClick={() => onNavigateToSubSection('revaluation')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                Revaluation
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <p className="text-muted-foreground">
                Adjust asset values to reflect current fair market value. Supports both upward and downward revaluations.
              </p>
              <Badge variant="outline" className="text-xs w-fit">Periodic/Ad-hoc</Badge>
              <div className="flex items-center gap-1 text-xs text-blue-600 font-medium pt-2">
                <span>View detailed guide</span>
                <ArrowRight className="h-3 w-3" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator className="my-8" />

      {/* Common Workflow */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Common Batch Workflow</h3>
        <p className="text-sm text-muted-foreground mb-4">
          All transaction types follow a similar batch-based workflow:
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">1. Create Batch</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">2. Add/Select Items</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">3. Enter Details</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">4. Review</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">5. Post</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          This structured approach ensures data accuracy, provides proper controls, and maintains a complete audit trail.
        </p>
      </div>
    </div>
  );
}

function DepreciationContent() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    const scrollContainer = document.querySelector('.overflow-y-auto') as HTMLElement;
    
    if (element && scrollContainer) {
      const yOffset = -20;
      const elementTop = element.offsetTop;
      const scrollPosition = elementTop + yOffset;
      
      scrollContainer.scrollTo({ 
        top: scrollPosition, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2">
          <TrendingDown className="h-6 w-6 text-orange-600" />
          Depreciation
        </h2>
        <p className="text-muted-foreground">
          Automated depreciation calculation and posting with full audit trail. Process periodic depreciation expenses 
          efficiently with batch processing and comprehensive validation.
        </p>
      </div>

      {/* Overview Card */}
      <Card className="relative overflow-hidden border-2 border-orange-200 dark:border-orange-800" id="overview">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-orange-600" />
            Module Overview
          </CardTitle>
          <CardDescription>Automated depreciation calculation and posting with full audit trail</CardDescription>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            The Depreciation module automates the calculation and posting of periodic depreciation expenses. 
            It supports multiple depreciation methods, batch processing, and provides a complete audit trail for compliance.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Calculator className="h-4 w-4 text-orange-600" />
                Automated Calculations
              </h5>
              <p className="text-xs text-muted-foreground">
                System automatically calculates depreciation based on asset settings, depreciation method, and period
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4 text-orange-600" />
                Batch Processing
              </h5>
              <p className="text-xs text-muted-foreground">
                Process multiple assets together in batches with validation and approval workflows
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-orange-600" />
                Journal Entries
              </h5>
              <p className="text-xs text-muted-foreground">
                Automatically generates journal entries for posting to your financial system
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <History className="h-4 w-4 text-orange-600" />
                Complete History
              </h5>
              <p className="text-xs text-muted-foreground">
                View all historical depreciation batches with full details and audit trail
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Page Layout */}
      <div id="page-layout">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-orange-600" />
          Page Layout & Navigation
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          The Depreciation page is divided into two main sections: the Current Period Summary at the top and the Transaction History below.
        </p>

        <div className="space-y-4">
          <div className="rounded-lg border p-4 bg-orange-50/50 dark:bg-orange-950/20">
            <h4 className="font-medium text-base mb-2">Current Period Summary</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Displays key metrics for the current depreciation period including total depreciation, asset count, and batch status.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <TrendingDown className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Period Depreciation:</strong> Total depreciation amount for the current period</span>
              </div>
              <div className="flex items-start gap-2">
                <Package className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Eligible Assets:</strong> Number of assets eligible for depreciation this period</span>
              </div>
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Batch Status:</strong> Current status of depreciation batches (Draft, Posted, etc.)</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-4 bg-orange-50/50 dark:bg-orange-950/20">
            <h4 className="font-medium text-base mb-2">Transaction History</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Lists all depreciation batches with search, filter, and sort capabilities. View, edit, or delete batches as needed.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <Search className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Search:</strong> Find batches by reference, date, or status</span>
              </div>
              <div className="flex items-start gap-2">
                <ArrowUpDown className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Sort:</strong> Click column headers to sort by any field</span>
              </div>
              <div className="flex items-start gap-2">
                <Eye className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>View Details:</strong> Click any batch to view full details and line items</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Workflow */}
      <div id="workflow">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5 text-orange-600" />
          Depreciation Workflow
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          The depreciation process follows a structured workflow to ensure accuracy and provide proper controls.
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">1. Create Batch</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">2. Select Assets</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">3. Calculate</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">4. Review</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">5. Post</Badge>
        </div>

        <StepByStepGuide
          title="Step-by-Step: Processing Depreciation"
          steps={[
            { step: 1, title: 'Create Batch', description: "Click 'Create Batch' button. The system creates a new depreciation batch for the current period.", hasScreenshot: true },
            { step: 2, title: 'Select Assets', description: "Click 'Add Assets' to select which assets to include. You can select all eligible assets or choose specific ones.", hasScreenshot: true },
            { step: 3, title: 'Review Calculations', description: 'The system automatically calculates depreciation for each asset. Review the amounts in the batch grid.', hasScreenshot: true },
            { step: 4, title: 'Edit if Needed', description: 'You can manually adjust depreciation amounts if required. Modified items are highlighted for audit purposes.', hasScreenshot: true },
            { step: 5, title: 'Save Draft', description: "Click 'Save' to save the batch as a draft. You can return to edit it later.", hasScreenshot: true },
            { step: 6, title: 'Process Batch', description: "When ready, click 'Process' to post the depreciation. This updates asset values and creates journal entries.", hasScreenshot: true },
          ]}
        />
      </div>

      <Separator />

      {/* Key Features */}
      <div id="key-features">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-orange-600" />
          Key Features
        </h3>

        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-lg mb-3">Asset Selection Dialog</h4>
            <p className="text-sm text-muted-foreground mb-3">
              The Asset Selection Dialog allows you to choose which assets to include in the depreciation batch.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Eligible Assets Only:</strong> Only shows assets eligible for depreciation in the current period</span>
              </div>
              <div className="flex items-start gap-2">
                <Search className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Search & Filter:</strong> Find specific assets by tag, description, category, or location</span>
              </div>
              <div className="flex items-start gap-2">
                <List className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Bulk Selection:</strong> Select all assets at once or choose individually</span>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Asset Details:</strong> View key information like current NBV, depreciation method, and remaining life</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-3">Batch Detail Grid</h4>
            <p className="text-sm text-muted-foreground mb-3">
              The Batch Detail Grid displays all assets in the current batch with their calculated depreciation amounts.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <Grid3x3 className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Editable Grid:</strong> Click any depreciation amount to edit it manually</span>
              </div>
              <div className="flex items-start gap-2">
                <Palette className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Modified Highlighting:</strong> Modified amounts are highlighted in yellow for easy identification</span>
              </div>
              <div className="flex items-start gap-2">
                <Trash2 className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Remove Items:</strong> Select and remove assets from the batch if needed</span>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Totals:</strong> View batch totals including total depreciation and asset count</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-3">Batch Actions</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Various actions are available depending on the batch status.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <Save className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Save Draft:</strong> Save the batch without posting (status: Draft)</span>
              </div>
              <div className="flex items-start gap-2">
                <Play className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Process:</strong> Post the depreciation and update asset values (status: Posted)</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Cancel:</strong> Discard the current batch without saving</span>
              </div>
              <div className="flex items-start gap-2">
                <Trash2 className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Delete:</strong> Delete a draft batch from the history (only available for drafts)</span>
              </div>
              <div className="flex items-start gap-2">
                <Download className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <span><strong>Export:</strong> Export batch details to Excel for reporting or analysis</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Depreciation Methods */}
      <div id="methods">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Calculator className="h-5 w-5 text-orange-600" />
          Depreciation Methods
        </h3>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Supported Calculation Methods</CardTitle>
            <CardDescription>Access Assets supports multiple depreciation calculation methods</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Each asset can use a different depreciation method based on accounting requirements. 
              See the <strong>Calculations</strong> section for detailed explanations, formulas, and examples of each method.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm">Straight-Line</h5>
                <p className="text-xs text-muted-foreground">Equal depreciation each period over the asset's useful life</p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm">Declining Balance</h5>
                <p className="text-xs text-muted-foreground">Higher depreciation in early years, declining over time</p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm">Sum of Years Digits</h5>
                <p className="text-xs text-muted-foreground">Accelerated depreciation based on remaining useful life</p>
              </div>
              <div className="rounded-lg border p-3">
                <h5 className="font-medium text-sm">Units of Production</h5>
                <p className="text-xs text-muted-foreground">Depreciation based on actual usage or production</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sticky Table of Contents */}
      <div className="hidden xl:block fixed right-8 top-64 w-56 z-10">
        <Card className="shadow-lg border-border/50">
          <CardHeader className="pb-3 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <List className="h-4 w-4 text-primary" />
              On This Page
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <nav className="space-y-1">
              <a
                href="#overview"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('overview');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Overview
                </span>
              </a>
              <a
                href="#page-layout"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('page-layout');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Page Layout
                </span>
              </a>
              <a
                href="#workflow"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('workflow');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Workflow
                </span>
              </a>
              <a
                href="#key-features"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('key-features');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Key Features
                </span>
              </a>
              <a
                href="#methods"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('methods');
                }}
                className="group block w-full text-left text-sm py-2 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-200 border-l-2 border-transparent hover:border-primary"
              >
                <span className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-primary transition-colors"></span>
                  Methods
                </span>
              </a>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdditionsContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8">
      <div id="additions-overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2">
          <Plus className="h-6 w-6 text-green-600" />
          Additions
        </h2>
        <p className="text-muted-foreground">
          Record new asset acquisitions and capital improvements. Create new assets or add value to existing ones 
          through upgrades, improvements, or enhancements.
        </p>
      </div>

      <Card className="relative overflow-hidden border-2 border-green-200 dark:border-green-800">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-green-600" />
            Module Overview
          </CardTitle>
          <CardDescription>Record new asset acquisitions and capital improvements</CardDescription>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            The Additions module allows you to record new asset purchases and add value to existing assets through upgrades, 
            improvements, or enhancements. It supports both creating new assets and capitalizing costs to existing assets.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <PlusCircle className="h-4 w-4 text-green-600" />
                New Asset Creation
              </h5>
              <p className="text-xs text-muted-foreground">
                Create brand new assets with full financial and operational details
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                Capital Improvements
              </h5>
              <p className="text-xs text-muted-foreground">
                Add value to existing assets through upgrades or enhancements
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-green-600" />
                Flexible Entry Types
              </h5>
              <p className="text-xs text-muted-foreground">
                Support for both period-based and ad-hoc additions
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <History className="h-4 w-4 text-green-600" />
                Complete Audit Trail
              </h5>
              <p className="text-xs text-muted-foreground">
                Track all additions with full history and documentation
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div id="addition-types">
        <h3 className="text-xl font-semibold mb-4">Addition Types</h3>
        <p className="text-sm text-muted-foreground mb-4">
          The Additions module supports two main types of additions, each serving different purposes.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border p-4 bg-green-50/50 dark:bg-green-950/20">
            <h4 className="font-medium text-base mb-2 flex items-center gap-2">
              <PlusCircle className="h-4 w-4 text-green-600" />
              New Asset Addition
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Create a completely new asset record with all required details.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Generates new asset tag automatically or manually</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Full asset details including category, location, and depreciation settings</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Creates initial journal entry for asset acquisition</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-4 bg-green-50/50 dark:bg-green-950/20">
            <h4 className="font-medium text-base mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Addition to Existing Asset
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Add value to an existing asset through improvements or upgrades.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Select existing asset from your register</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Increases asset's acquisition cost and net book value</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Adjusts remaining useful life if needed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      <div id="workflow">
        <h3 className="text-xl font-semibold mb-4">Workflow</h3>
        <div className="flex flex-wrap gap-2 mb-6">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">1. Create Batch</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">2. Add Items</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">3. Enter Details</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">4. Review</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">5. Post</Badge>
        </div>

        <StepByStepGuide
          title="Step-by-Step: Processing Additions"
          steps={[
            { step: 1, title: 'Create Batch', description: "Click 'Create Batch' button. Choose between Period-based or Ad-hoc addition type.", hasScreenshot: true },
            { step: 2, title: 'Add New Items', description: "Click 'Add New Asset' to create a new asset or 'Add to Existing' to capitalize costs to an existing asset.", hasScreenshot: true },
            { step: 3, title: 'Enter Asset Details', description: 'For new assets, enter tag, description, category, location, and other required fields.', hasScreenshot: true },
            { step: 4, title: 'Enter Financial Details', description: 'Enter acquisition cost, acquisition date, depreciation method, useful life, and salvage value.', hasScreenshot: true },
            { step: 5, title: 'Save Draft', description: "Click 'Save' to save the batch as a draft. You can return to edit it later.", hasScreenshot: true },
            { step: 6, title: 'Process Batch', description: "When ready, click 'Process' to post the additions. This creates/updates assets and generates journal entries.", hasScreenshot: true },
          ]}
        />
      </div>

      {/* Sticky Table of Contents */}
      <div className="hidden xl:block fixed right-8 top-64 w-56">
        <div className="space-y-2">
          <h4 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wide">
            On This Page
          </h4>
          <nav className="space-y-1">
            <button
              onClick={() => scrollToSection('additions-overview')}
              className="block w-full text-left text-sm py-1.5 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Overview
            </button>
            <button
              onClick={() => scrollToSection('addition-types')}
              className="block w-full text-left text-sm py-1.5 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Addition Types
            </button>
            <button
              onClick={() => scrollToSection('workflow')}
              className="block w-full text-left text-sm py-1.5 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Workflow
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

function RevaluationContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8">
      <div id="revaluation-overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2">
          <RefreshCw className="h-6 w-6 text-blue-600" />
          Revaluation
        </h2>
        <p className="text-muted-foreground">
          Adjust asset values to reflect current fair market value. Support for both upward and downward revaluations 
          with comprehensive documentation and approval workflows.
        </p>
      </div>

      <Card className="relative overflow-hidden border-2 border-blue-200 dark:border-blue-800">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            Module Overview
          </CardTitle>
          <CardDescription>Adjust asset values to reflect current fair market value</CardDescription>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            The Revaluation module allows you to adjust the book value of assets to reflect their current fair market value. 
            This is essential for maintaining accurate financial statements and complying with accounting standards like IAS 16.
          </p>
          <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle>Important Note</AlertTitle>
            <AlertDescription>
              Revaluations may have significant tax and accounting implications. Always consult with your finance team 
              and external auditors before processing revaluations.
            </AlertDescription>
          </Alert>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                Upward Revaluation
              </h5>
              <p className="text-xs text-muted-foreground">
                Increase asset value when fair market value exceeds book value
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-blue-600" />
                Downward Revaluation
              </h5>
              <p className="text-xs text-muted-foreground">
                Decrease asset value when fair market value is below book value
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Calculator className="h-4 w-4 text-blue-600" />
                Flexible Entry Modes
              </h5>
              <p className="text-xs text-muted-foreground">
                Enter new NBV directly or specify adjustment amount
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-background/50">
              <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                Automatic Calculations
              </h5>
              <p className="text-xs text-muted-foreground">
                System calculates revaluation adjustments automatically
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div id="entry-methods">
        <h3 className="text-xl font-semibold mb-4">Revaluation Entry Methods</h3>
        <p className="text-sm text-muted-foreground mb-4">
          The Revaluation module supports two entry methods for maximum flexibility.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
            <h4 className="font-medium text-base mb-2 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-blue-600" />
              New Net Book Value
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Enter the new fair market value directly. The system calculates the adjustment amount.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <span>Ideal when you have a professional valuation</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <span>System calculates revaluation adjustment automatically</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <span>Shows increase or decrease from current NBV</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
            <h4 className="font-medium text-base mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              Adjustment Amount
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Enter the adjustment amount (positive or negative). System calculates new NBV.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <span>Useful when you know the specific adjustment needed</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <span>Positive values increase asset value</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <span>Negative values decrease asset value</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      <div id="workflow">
        <h3 className="text-xl font-semibold mb-4">Workflow</h3>
        <div className="flex flex-wrap gap-2 mb-6">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">1. Create Batch</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">2. Select Assets</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">3. Enter Values</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">4. Review</Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">5. Post</Badge>
        </div>

        <StepByStepGuide
          title="Step-by-Step: Processing Revaluations"
          steps={[
            { step: 1, title: 'Create Batch', description: "Click 'Create Batch' button. The system creates a new revaluation batch.", hasScreenshot: true },
            { step: 2, title: 'Select Assets', description: "Click 'Add Assets' to select which assets to revalue. Search and filter to find specific assets.", hasScreenshot: true },
            { step: 3, title: 'Choose Entry Mode', description: "Select whether to enter 'New NBV' or 'Adjustment Amount' for each asset.", hasScreenshot: true },
            { step: 4, title: 'Enter Revaluation Values', description: 'Enter the new values. The system automatically calculates the revaluation adjustment.', hasScreenshot: true },
            { step: 5, title: 'Add Notes', description: 'Add notes explaining the reason for revaluation (e.g., professional valuation, market conditions).', hasScreenshot: true },
            { step: 6, title: 'Save Draft', description: "Click 'Save' to save the batch as a draft for review and approval.", hasScreenshot: true },
            { step: 7, title: 'Process Batch', description: "When approved, click 'Process' to post the revaluation and update asset values.", hasScreenshot: true },
          ]}
        />
      </div>

      {/* Sticky Table of Contents */}
      <div className="hidden xl:block fixed right-8 top-64 w-56">
        <div className="space-y-2">
          <h4 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wide">
            On This Page
          </h4>
          <nav className="space-y-1">
            <button
              onClick={() => scrollToSection('revaluation-overview')}
              className="block w-full text-left text-sm py-1.5 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Overview
            </button>
            <button
              onClick={() => scrollToSection('entry-methods')}
              className="block w-full text-left text-sm py-1.5 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Entry Methods
            </button>
            <button
              onClick={() => scrollToSection('workflow')}
              className="block w-full text-left text-sm py-1.5 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Workflow
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

interface AuditsContentProps {
  onNavigateToSubSection?: (subSectionId: string) => void;
}

function AuditsContent({ onNavigateToSubSection }: AuditsContentProps) {
  return (
    <div className="space-y-8 relative">
      <div id="overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Audits Management</h2>
        <p className="text-muted-foreground">
          Plan, execute, and track physical asset audits to verify existence, location, and condition of your assets. Click on any audit module below to view detailed documentation.
        </p>
      </div>

      <Separator />

      {/* Audit Modules - Clickable Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card 
          className="relative overflow-hidden border-2 border-blue-200 dark:border-blue-800 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onNavigateToSubSection?.('create-audit')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <Plus className="h-5 w-5 text-blue-600" />
              Create Audit
            </CardTitle>
          </CardHeader>
          <CardContent className="relative space-y-3">
            <p className="text-sm text-muted-foreground">
              Create new audits, define scope, configure settings, and set up audit parameters for physical verification.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">New Audit</Badge>
              <Badge variant="outline" className="text-xs">Scope Definition</Badge>
              <Badge variant="outline" className="text-xs">Configuration</Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-600 font-medium pt-2">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-green-200 dark:border-green-800 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onNavigateToSubSection?.('active-audits')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <Play className="h-5 w-5 text-green-600" />
              Active Audits
            </CardTitle>
          </CardHeader>
          <CardContent className="relative space-y-3">
            <p className="text-sm text-muted-foreground">
              Monitor and manage ongoing audits, track progress, verify assets, and record findings in real-time.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">In Progress</Badge>
              <Badge variant="outline" className="text-xs">Asset Verification</Badge>
              <Badge variant="outline" className="text-xs">Progress Tracking</Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-600 font-medium pt-2">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-orange-200 dark:border-orange-800 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onNavigateToSubSection?.('scheduled-audits')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-5 w-5 text-orange-600" />
              Scheduled Audits
            </CardTitle>
          </CardHeader>
          <CardContent className="relative space-y-3">
            <p className="text-sm text-muted-foreground">
              Plan future audits, set up recurring schedules, manage audit calendar, and configure automated reminders.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">Scheduling</Badge>
              <Badge variant="outline" className="text-xs">Recurring Audits</Badge>
              <Badge variant="outline" className="text-xs">Reminders</Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-orange-600 font-medium pt-2">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onNavigateToSubSection?.('audit-history')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-5 w-5 text-purple-600" />
              Audit History
            </CardTitle>
          </CardHeader>
          <CardContent className="relative space-y-3">
            <p className="text-sm text-muted-foreground">
              Review completed audits, analyze findings, and access historical audit reports and activity logs.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">Completed Audits</Badge>
              <Badge variant="outline" className="text-xs">Reports</Badge>
              <Badge variant="outline" className="text-xs">Activity Logs</Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-purple-600 font-medium pt-2">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-red-200 dark:border-red-800 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => onNavigateToSubSection?.('audit-exceptions')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Exceptions
            </CardTitle>
          </CardHeader>
          <CardContent className="relative space-y-3">
            <p className="text-sm text-muted-foreground">
              Manage audit exceptions and discrepancies. Track missing assets, location mismatches, and condition issues.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">Exception Tracking</Badge>
              <Badge variant="outline" className="text-xs">Resolution</Badge>
              <Badge variant="outline" className="text-xs">Reporting</Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-red-600 font-medium pt-2">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Common Audit Features */}
      <div id="common-features">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Info className="h-5 w-5 text-green-600" />
          Common Audit Features
        </h3>
        
        <p className="text-muted-foreground mb-4">
          All audit modules share common features for creating, managing, and tracking audits. Select a specific audit module above to view detailed documentation for Audit History or Exceptions.
        </p>
      </div>
    </div>
  );
}

// Audit History Content Component
function AuditHistoryContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 relative">
      <div id="overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Audit History</h2>
        <p className="text-muted-foreground">
          Review completed audits, analyze findings, access historical reports, and maintain comprehensive audit trails for compliance and analysis.
        </p>
      </div>

      <Separator />

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-5 w-5 text-purple-600" />
              Complete History
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Access all completed audits with full details, findings, and activity logs.
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-blue-200 dark:border-blue-800">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-blue-600" />
              Audit Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Generate comprehensive reports with summaries, exceptions, and compliance data.
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-green-200 dark:border-green-800">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <Search className="h-5 w-5 text-green-600" />
              Advanced Search
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Find audits quickly using powerful search and filtering capabilities.
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-orange-200 dark:border-orange-800">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <Download className="h-5 w-5 text-orange-600" />
              Export Data
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Export audit data to CSV or Excel for reporting and analysis.
            </p>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Page Layout */}
      <div id="page-layout">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <LayoutDashboard className="h-5 w-5 text-purple-600" />
          Page Layout & Navigation
        </h3>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Main Grid View</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                The Audit History page displays all completed audits in a comprehensive grid with sortable columns and advanced filtering.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Search Bar:</strong> Quick search across audit names, types, and assignees
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Action Buttons:</strong> Export data, toggle right panel, and access quick actions
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Data Grid:</strong> Sortable columns with audit details, completion dates, and statistics
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Pagination:</strong> Navigate through large datasets with configurable page sizes
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Right Panel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                The right panel provides advanced filtering and column customization options.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Filters Tab:</strong> Filter by audit type, status, date range, assignee, and more
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Columns className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Columns Tab:</strong> Show/hide columns and reorder them to match your workflow
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Workflow */}
      <div id="workflow">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-purple-600" />
          Workflow Guide
        </h3>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">Step 1</Badge>
                Search & Filter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Use the search bar and filters to find specific audits.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Quick Search:</strong> Type audit name, type, or assignee in the search bar</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Advanced Filters:</strong> Open right panel and apply filters by date range, audit type, status, etc.</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">Step 2</Badge>
                Review Audit Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Click on any audit row to view comprehensive details.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Audit Summary:</strong> View completion statistics, dates, and assignees</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Findings:</strong> Review exceptions and discrepancies found during the audit</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">Step 3</Badge>
                View Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Access the complete audit trail for compliance and tracking.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Timeline View:</strong> See chronological log of all audit activities</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>User Tracking:</strong> Identify who performed each action and when</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">Step 4</Badge>
                Generate Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Create comprehensive audit reports for stakeholders.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Summary Report:</strong> Overview of audit results and completion statistics</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Exception Report:</strong> Detailed list of all exceptions found</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">Step 5</Badge>
                Export Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Export audit data for further analysis and reporting.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>CSV Export:</strong> Export to CSV for use in spreadsheets</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Excel Export:</strong> Export to Excel with formatted data</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Right Panel - On This Page */}
      <div className="fixed right-8 top-64 hidden xl:block w-64">
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <h4 className="text-sm font-semibold mb-3">On This Page</h4>
          <nav className="space-y-2">
            <button
              onClick={() => scrollToSection('overview')}
              className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left w-full"
            >
              Overview
            </button>
            <button
              onClick={() => scrollToSection('page-layout')}
              className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left w-full"
            >
              Page Layout
            </button>
            <button
              onClick={() => scrollToSection('workflow')}
              className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left w-full"
            >
              Workflow
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

// Audit Exceptions Content Component
function AuditExceptionsContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 relative">
      <div id="overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Audit Exceptions</h2>
        <p className="text-muted-foreground">
          Manage audit exceptions and discrepancies. Track missing assets, location mismatches, condition issues, and data discrepancies found during physical audits.
        </p>
      </div>

      <Separator />

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="relative overflow-hidden border-2 border-red-200 dark:border-red-800">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Exception Tracking
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Track and manage all audit exceptions with detailed categorization and priority levels.
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-orange-200 dark:border-orange-800">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="h-5 w-5 text-orange-600" />
              Resolution Workflow
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Follow structured workflows to resolve exceptions and update asset records.
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-blue-200 dark:border-blue-800">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <UserPlus className="h-5 w-5 text-blue-600" />
              Assignment & Collaboration
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Assign exceptions to team members and collaborate on resolution.
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-green-200 dark:border-green-800">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-green-600" />
              Reporting
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground">
              Generate exception reports and export data for analysis and compliance.
            </p>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Page Layout */}
      <div id="page-layout">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <LayoutDashboard className="h-5 w-5 text-red-600" />
          Page Layout & Navigation
        </h3>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Metrics Cards</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                The top of the page displays key exception metrics in color-coded cards.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Total Exceptions:</strong> Overall count of all exceptions
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Open:</strong> Exceptions awaiting action
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>In Progress:</strong> Exceptions currently being resolved
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Resolved:</strong> Completed exceptions
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">View Modes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Switch between card and list views to match your workflow preference.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <LayoutGrid className="h-4 w-4 text-red-600" />
                    <h5 className="font-medium text-sm">Card View</h5>
                  </div>
                  <p className="text-xs text-muted-foreground">Visual cards with exception details, status, and quick actions</p>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <LayoutList className="h-4 w-4 text-red-600" />
                    <h5 className="font-medium text-sm">List View</h5>
                  </div>
                  <p className="text-xs text-muted-foreground">Compact table with sortable columns and bulk selection</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Right Panel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Access advanced filtering and column customization options.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Filters Tab:</strong> Filter by exception type, status, severity, priority, assignee, and date range
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Columns className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <strong>Columns Tab:</strong> Customize visible columns in list view
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Exception Types */}
      <div id="exception-types">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          Exception Types
        </h3>

        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  Missing Asset
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Asset cannot be located during physical verification. Requires investigation and potential write-off.
                </p>
                <Badge variant="destructive" className="text-xs">Critical</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  Location Mismatch
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Asset found in a different location than recorded in the system. Requires location update.
                </p>
                <Badge variant="outline" className="text-xs border-orange-300 text-orange-700">High</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  Condition Issue
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Asset is damaged, in poor condition, or not functioning properly. May require repair or disposal.
                </p>
                <Badge variant="outline" className="text-xs border-yellow-300 text-yellow-700">Medium</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  Data Discrepancy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Asset details (serial number, model, description) do not match physical asset. Requires data correction.
                </p>
                <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">Medium</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Separator />

      {/* Workflow */}
      <div id="workflow">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-red-600" />
          Exception Resolution Workflow
        </h3>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Step 1</Badge>
                Review Exception
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Click on an exception to view full details and context.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Exception Details:</strong> View type, severity, priority, and description</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Asset Information:</strong> See related asset details and location</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Audit Context:</strong> View the audit where the exception was found</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Step 2</Badge>
                Assign for Resolution
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Assign the exception to a team member for investigation and resolution.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Select Assignee:</strong> Choose user or team responsible for resolution</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Set Priority:</strong> Adjust priority level if needed</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Add Notes:</strong> Provide context or instructions for the assignee</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Step 3</Badge>
                Investigate & Document
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Investigate the exception and document findings.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Update Status:</strong> Change status to "In Progress" while investigating</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Add Comments:</strong> Document investigation findings and actions taken</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Attach Evidence:</strong> Upload photos or documents as needed</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Step 4</Badge>
                Resolve Exception
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Take corrective action and mark the exception as resolved.
              </p>
              <div className="grid gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Corrective Action:</strong> Update asset records, location, or condition as needed</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Resolution Notes:</strong> Document how the exception was resolved</span>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Mark Resolved:</strong> Change status to "Resolved" and close the exception</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Right Panel - On This Page */}
      <div className="fixed right-8 top-64 hidden xl:block w-64">
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <h4 className="text-sm font-semibold mb-3">On This Page</h4>
          <nav className="space-y-2">
            <button
              onClick={() => scrollToSection('overview')}
              className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left w-full"
            >
              Overview
            </button>
            <button
              onClick={() => scrollToSection('page-layout')}
              className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left w-full"
            >
              Page Layout
            </button>
            <button
              onClick={() => scrollToSection('exception-types')}
              className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left w-full"
            >
              Exception Types
            </button>
            <button
              onClick={() => scrollToSection('workflow')}
              className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left w-full"
            >
              Workflow
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

// Create Audit Content Component
function CreateAuditContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 relative">
      <div id="overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Create Audit</h2>
        <p className="text-muted-foreground">
          Create new physical asset audits using a guided wizard process. Define audit scope, schedule, assign team members, and configure audit parameters for comprehensive asset verification.
        </p>
      </div>

      <Separator />

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-blue-600" />
              Wizard Process
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              5-step guided wizard to create audits with validation and draft saving
            </p>
          </CardContent>
        </Card>

        <Card className="border-green-200 dark:border-green-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FolderTree className="h-4 w-4 text-green-600" />
              Flexible Scope
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Define audit scope by location, category, department, or custom asset selection
            </p>
          </CardContent>
        </Card>

        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-orange-600" />
              Scheduling Options
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Schedule audits immediately, for later, or set up recurring audit patterns
            </p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-600" />
              Team Assignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Assign individual auditors or entire teams with notification options
            </p>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Wizard Steps */}
      <div id="wizard-steps">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Repeat className="h-5 w-5 text-blue-600" />
          5-Step Wizard Process
        </h3>

        <div className="space-y-6">
          {/* Step 1 */}
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-100 text-blue-700">Step 1</Badge>
                Audit Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Define the basic information for your audit including title, description, type, and priority.
              </p>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Key Fields:</h4>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li><strong>Audit Title:</strong> Descriptive name for the audit</li>
                  <li><strong>Description:</strong> Detailed purpose and objectives</li>
                  <li><strong>Audit Type:</strong> Physical Verification, Condition Assessment, Location Verification, or Full Audit</li>
                  <li><strong>Priority:</strong> Low, Medium, High, or Critical</li>
                  <li><strong>Template:</strong> Optional - Use predefined audit templates</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Step 2 */}
          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-green-100 text-green-700">Step 2</Badge>
                Define Scope
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Select which assets will be included in the audit using various filtering options.
              </p>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Scope Options:</h4>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li><strong>All Assets:</strong> Include all assets in the system</li>
                  <li><strong>By Location:</strong> Select specific locations or buildings</li>
                  <li><strong>By Department:</strong> Filter by organizational departments</li>
                  <li><strong>By Category:</strong> Select asset categories (IT Equipment, Furniture, etc.)</li>
                  <li><strong>Custom Selection:</strong> Manually select specific assets</li>
                </ul>
              </div>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  The system will display the total number of assets included based on your scope selection.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Step 3 */}
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-orange-100 text-orange-700">Step 3</Badge>
                Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Set the audit timeline and configure recurring patterns if needed.
              </p>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Schedule Types:</h4>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li><strong>Immediate:</strong> Start the audit right away</li>
                  <li><strong>Scheduled:</strong> Set specific start and due dates</li>
                  <li><strong>Recurring:</strong> Configure automatic recurring audits</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Recurring Options:</h4>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li>Daily, Weekly, Monthly, Quarterly, or Yearly patterns</li>
                  <li>Set recurrence interval (e.g., every 2 weeks)</li>
                  <li>Define end date or number of occurrences</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Step 4 */}
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-100 text-purple-700">Step 4</Badge>
                Assign Team
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Assign auditors who will perform the physical verification and record findings.
              </p>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Assignment Options:</h4>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li><strong>Individual Users:</strong> Select specific team members</li>
                  <li><strong>Teams:</strong> Assign entire teams to the audit</li>
                  <li><strong>Notifications:</strong> Send email notifications to assignees</li>
                  <li><strong>Completion Alerts:</strong> Notify on audit completion</li>
                  <li><strong>Reassignment:</strong> Allow auditors to reassign tasks</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Step 5 */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-indigo-100 text-indigo-700">Step 5</Badge>
                Review & Submit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Review all audit details before creating the audit. Make any necessary changes before submission.
              </p>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Review Summary Includes:</h4>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li>Audit details (title, type, priority)</li>
                  <li>Scope summary (number of assets, locations)</li>
                  <li>Schedule information (dates, recurrence)</li>
                  <li>Assigned team members</li>
                  <li>Notification settings</li>
                </ul>
              </div>
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  You can save the audit as a draft at any step and return to complete it later.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Key Features */}
      <div id="key-features">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-blue-600" />
          Key Features
        </h3>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Save className="h-4 w-4 text-blue-600" />
                Draft Saving
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Your progress is automatically saved as you move through the wizard steps. You can exit at any time and resume later.
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Auto-save on step navigation</li>
                <li>Manual save option available</li>
                <li>Resume from last saved step</li>
                <li>Clear draft to start fresh</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Validation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Each step includes validation to ensure all required information is provided before proceeding.
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Required field validation</li>
                <li>Scope selection validation</li>
                <li>Date range validation</li>
                <li>Team assignment validation</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-orange-600" />
                Templates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Use predefined audit templates to quickly create audits with common configurations.
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Pre-configured audit types</li>
                <li>Standard scope definitions</li>
                <li>Default scheduling patterns</li>
                <li>Save custom templates</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4 text-purple-600" />
                Progress Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Visual progress indicator shows your completion status through the wizard steps.
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Step-by-step progress bar</li>
                <li>Completed step indicators</li>
                <li>Current step highlighting</li>
                <li>Navigation between steps</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Right Panel - On This Page */}
      <div className="hidden xl:block fixed right-8 top-64 w-64">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-1">
              <button
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Overview
              </button>
              <button
                onClick={() => scrollToSection('wizard-steps')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Wizard Steps
              </button>
              <button
                onClick={() => scrollToSection('key-features')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Key Features
              </button>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Active Audits Content Component
function ActiveAuditsContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 relative">
      <div id="overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Active Audits</h2>
        <p className="text-muted-foreground">
          Monitor and manage ongoing audits, track progress, verify assets, record findings, and collaborate with team members in real-time during physical asset verification.
        </p>
      </div>

      <Separator />

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-green-200 dark:border-green-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Play className="h-4 w-4 text-green-600" />
              Real-Time Tracking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Monitor audit progress with live updates and completion percentages
            </p>
          </CardContent>
        </Card>

        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <LayoutList className="h-4 w-4 text-blue-600" />
              Multiple Views
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Switch between list, card, and kanban views for different perspectives
            </p>
          </CardContent>
        </Card>

        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Filter className="h-4 w-4 text-orange-600" />
              Advanced Filtering
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Filter by status, priority, assignee, due date, and more
            </p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-600" />
              Team Collaboration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Assign, reassign, and track team member contributions
            </p>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Page Layout */}
      <div id="page-layout">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-green-600" />
          Page Layout & Navigation
        </h3>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Header Section</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong>Create Audit Button:</strong> Quick access to create new audits</li>
                <li><strong>View Mode Toggle:</strong> Switch between List, Card, and Kanban views</li>
                <li><strong>Search Bar:</strong> Search audits by title or description</li>
                <li><strong>Quick Filters:</strong> Filter by due date (Overdue, Due Today, Due This Week)</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Main Content Area</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong>List View:</strong> Tabular display with sortable columns</li>
                <li><strong>Card View:</strong> Visual cards with key information</li>
                <li><strong>Kanban View:</strong> Drag-and-drop board organized by status</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Right Panel</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong>Filters Tab:</strong> Advanced filtering options</li>
                <li><strong>Columns Tab:</strong> Customize visible columns (List view only)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Workflow */}
      <div id="workflow">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Repeat className="h-5 w-5 text-green-600" />
          Audit Management Workflow
        </h3>

        <div className="space-y-4">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-green-100 text-green-700">1</Badge>
                View Active Audits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Browse all in-progress audits with status indicators, progress bars, and key metrics. Use filters and search to find specific audits.
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-100 text-blue-700">2</Badge>
                Select Audit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Click on an audit to view detailed information, asset list, team members, and progress. Access audit actions from the context menu.
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-orange-100 text-orange-700">3</Badge>
                Perform Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Take actions on audits: Start/Resume, Pause, Complete, Reassign team members, or Delete. Each action updates the audit status accordingly.
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-100 text-purple-700">4</Badge>
                Track Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Monitor completion percentage, verified assets, flagged items, and missing assets. View real-time updates as team members record findings.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Key Features */}
      <div id="key-features">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-green-600" />
          Key Features
        </h3>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <LayoutList className="h-4 w-4 text-blue-600" />
                View Modes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Choose the view that works best for your workflow:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong>List View:</strong> Detailed table with sortable columns</li>
                <li><strong>Card View:</strong> Visual cards with key metrics</li>
                <li><strong>Kanban View:</strong> Drag-and-drop board by status</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4 text-orange-600" />
                Filtering & Search
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Powerful filtering options to find audits quickly:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Filter by status, priority, type</li>
                <li>Filter by assignee or team</li>
                <li>Quick filters for due dates</li>
                <li>Full-text search</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-600" />
                Team Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Manage audit team assignments and collaboration:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>View assigned team members</li>
                <li>Reassign audits to different users</li>
                <li>Add additional team members</li>
                <li>Track individual contributions</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Download className="h-4 w-4 text-green-600" />
                Export & Reporting
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Export audit data for reporting and analysis:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Export to CSV or Excel</li>
                <li>Include filtered results only</li>
                <li>Bulk export multiple audits</li>
                <li>Generate progress reports</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Right Panel - On This Page */}
      <div className="hidden xl:block fixed right-8 top-64 w-64">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-1">
              <button
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Overview
              </button>
              <button
                onClick={() => scrollToSection('page-layout')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Page Layout
              </button>
              <button
                onClick={() => scrollToSection('workflow')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Workflow
              </button>
              <button
                onClick={() => scrollToSection('key-features')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Key Features
              </button>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Scheduled Audits Content Component
function ScheduledAuditsContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 relative">
      <div id="overview">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Scheduled Audits</h2>
        <p className="text-muted-foreground">
          Plan and manage future audits, set up recurring audit schedules, view audit calendar, and configure automated reminders for upcoming physical asset verifications.
        </p>
      </div>

      <Separator />

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-orange-600" />
              Calendar View
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Visual calendar display of scheduled and recurring audits
            </p>
          </CardContent>
        </Card>

        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Repeat className="h-4 w-4 text-blue-600" />
              Recurring Audits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Set up automatic recurring audit patterns with flexible schedules
            </p>
          </CardContent>
        </Card>

        <Card className="border-green-200 dark:border-green-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-600" />
              Reminders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Automated notifications for upcoming audits and deadlines
            </p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Edit className="h-4 w-4 text-purple-600" />
              Easy Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Edit, reschedule, or cancel future audits with simple actions
            </p>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Page Layout */}
      <div id="page-layout">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-orange-600" />
          Page Layout & Navigation
        </h3>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">View Modes</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong>Calendar View:</strong> Monthly calendar with audit markers</li>
                <li><strong>List View:</strong> Chronological list of scheduled audits</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Calendar Features</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Month navigation (previous/next)</li>
                <li>Today button for quick navigation</li>
                <li>Color-coded audit indicators</li>
                <li>Click dates to view audit details</li>
                <li>Hover for quick audit preview</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">List View Features</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Grouped by date (Today, Tomorrow, This Week, etc.)</li>
                <li>Audit cards with key information</li>
                <li>Quick action buttons</li>
                <li>Recurring audit indicators</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Recurring Audits */}
      <div id="recurring-audits">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Repeat className="h-5 w-5 text-orange-600" />
          Recurring Audit Patterns
        </h3>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Set up audits that automatically repeat on a regular schedule. Perfect for compliance audits, routine verifications, and periodic checks.
            </p>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Recurrence Patterns</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong>Daily:</strong> Repeat every day or every X days</li>
                <li><strong>Weekly:</strong> Repeat on specific days of the week</li>
                <li><strong>Monthly:</strong> Repeat on specific day of month</li>
                <li><strong>Quarterly:</strong> Repeat every 3 months</li>
                <li><strong>Yearly:</strong> Repeat annually on specific date</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">End Conditions</h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li><strong>Never:</strong> Continue indefinitely</li>
                <li><strong>After X occurrences:</strong> Stop after specific number</li>
                <li><strong>On specific date:</strong> End on particular date</li>
              </ul>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Recurring audits are automatically created based on the schedule. You can edit or cancel individual occurrences without affecting the series.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Workflow */}
      <div id="workflow">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Repeat className="h-5 w-5 text-orange-600" />
          Scheduling Workflow
        </h3>

        <div className="space-y-4">
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-orange-100 text-orange-700">1</Badge>
                View Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Browse scheduled audits in calendar or list view. Use filters to show specific audit types, priorities, or assignees.
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-100 text-blue-700">2</Badge>
                Create Scheduled Audit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Click "Create Audit" and select "Scheduled" or "Recurring" in the schedule step. Configure dates, times, and recurrence patterns.
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-green-100 text-green-700">3</Badge>
                Manage Audits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Edit audit details, reschedule to different dates, modify recurrence patterns, or cancel scheduled audits as needed.
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="bg-purple-100 text-purple-700">4</Badge>
                Start Audits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                When the scheduled date arrives, audits automatically move to "Active" status. Team members receive notifications to begin verification.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Key Features */}
      <div id="key-features">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-orange-600" />
          Key Features
        </h3>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-orange-600" />
                Visual Calendar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Interactive calendar view with audit indicators:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Color-coded by audit type</li>
                <li>Multiple audits per day</li>
                <li>Quick preview on hover</li>
                <li>Click to view details</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Repeat className="h-4 w-4 text-blue-600" />
                Recurring Schedules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Flexible recurring audit configuration:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Multiple frequency options</li>
                <li>Custom intervals</li>
                <li>End date or occurrence limit</li>
                <li>Edit series or single occurrence</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-green-600" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Automated reminders and notifications:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Upcoming audit reminders</li>
                <li>Start date notifications</li>
                <li>Due date alerts</li>
                <li>Team member notifications</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Edit className="h-4 w-4 text-purple-600" />
                Easy Rescheduling
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Flexible audit management options:
              </p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Drag-and-drop rescheduling</li>
                <li>Edit audit details</li>
                <li>Cancel individual occurrences</li>
                <li>Modify recurring patterns</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Right Panel - On This Page */}
      <div className="hidden xl:block fixed right-8 top-64 w-64">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-1">
              <button
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Overview
              </button>
              <button
                onClick={() => scrollToSection('page-layout')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Page Layout
              </button>
              <button
                onClick={() => scrollToSection('recurring-audits')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Recurring Audits
              </button>
              <button
                onClick={() => scrollToSection('workflow')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Workflow
              </button>
              <button
                onClick={() => scrollToSection('key-features')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Key Features
              </button>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface SettingsContentProps {
  readonly onNavigateToSubSection?: (subSectionId: string) => void;
}

function AssetOptionsContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Asset Options</h2>
        <p className="text-muted-foreground">
          Configure comprehensive asset settings, financial integrations, and system-wide defaults for your asset management system.
        </p>
      </div>

      <Separator />

      {/* Overview Section */}
      <div id="overview">
        <h3 className="text-lg font-semibold mb-4">Overview</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Asset Options is the central configuration hub for your asset management system. It provides comprehensive settings across five key areas: Details, Transactions, Periods, Integrations, and Language. These settings control how assets are created, depreciated, and integrated with your financial systems.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">General Configuration</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Configure asset numbering, validation rules, and default behaviors for asset creation and management.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileEdit className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Transaction Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Define nominal accounts, costing centers, and posting rules for depreciation, additions, and disposals.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Period Configuration</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Set up depreciation period patterns (4-4-5, monthly, quarterly) and manage financial year structures.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Plug2 className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Financial Integrations</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Connect to Access Financials, Access Dimensions, Xero, or QuickBooks for seamless data synchronization.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Configuration Tabs Section */}
      <div id="configuration-tabs">
        <h3 className="text-lg font-semibold mb-4">Configuration Tabs</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Asset Options is organized into five tabs, each focusing on a specific aspect of system configuration. Navigate between tabs using the vertical sidebar on the left.
        </p>

        <div className="space-y-6">
          {/* Details Tab */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Tab 1</Badge>
                <CardTitle className="text-base">Details</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Configure general asset settings, nominal update prompts, and default depreciation parameters.
              </p>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold mb-2">General Settings</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>Asset Auto Numbering:</strong> Automatically generate sequential asset numbers</li>
                    <li>• <strong>Prevent Negative Values:</strong> Block negative bought values to ensure data integrity</li>
                    <li>• <strong>Default Base Value:</strong> Choose between remaining or original postings for calculations</li>
                    <li>• <strong>Service Period Defaults:</strong> Set default service period in months</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Nominal Update Prompts</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Enable/disable prompts for depreciation, additions, revaluations, disposals, and transfers</li>
                    <li>• Control whether to include resource information on nominal journals</li>
                    <li>• Set default journal summarization behavior</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Default Depreciation Settings</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>Default Method:</strong> Straight Line, Reducing Balance, or other methods</li>
                    <li>• <strong>Default Percentage:</strong> Annual depreciation rate (0-100%)</li>
                    <li>• <strong>Default Period:</strong> Asset useful life in years</li>
                    <li>• <strong>Default Frequency:</strong> Monthly, Quarterly, or Annual depreciation</li>
                    <li>• <strong>Do Not Depreciate:</strong> Option to exclude assets from depreciation by default</li>
                    <li>• <strong>Force Transfers:</strong> Require transfer transactions when changing categories</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transactions Tab */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Tab 2</Badge>
                <CardTitle className="text-base">Transactions</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Define nominal accounts, cost centers, and posting rules for all transaction types.
              </p>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold mb-2">Transaction Behavior</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>Allow Zero Value Additions:</strong> Permit adding assets with zero initial cost</li>
                    <li>• <strong>Force Depreciation Before Period End:</strong> Require all depreciation to be posted before closing periods</li>
                    <li>• <strong>Preserve In-Period Depreciation:</strong> Prevent reversal of depreciation within the same period</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Nominal Accounts</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>Asset Cost:</strong> Balance sheet account for asset capitalization</li>
                    <li>• <strong>Depreciation B/S:</strong> Accumulated depreciation account</li>
                    <li>• <strong>Depreciation P&L:</strong> Depreciation expense account</li>
                    <li>• <strong>Disposal:</strong> Account for asset disposal transactions</li>
                    <li>• <strong>Revaluation B/S:</strong> Revaluation reserve account</li>
                    <li>• <strong>Revaluation P&L:</strong> Revaluation gain/loss account</li>
                    <li>• <strong>Disposals Bank Account:</strong> Bank account for disposal proceeds</li>
                    <li>• <strong>Additions WIP/Bank:</strong> Work-in-progress or bank account for additions</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Costing Centers</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Assign default cost centers for depreciation, additions, disposals, and revaluations</li>
                    <li>• Enable cost center tracking for departmental reporting</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Posting Options</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>Post Costing Transactions:</strong> Automatically post to cost centers</li>
                    <li>• <strong>Post CREDIT to Costing:</strong> Post credit entries to cost centers</li>
                    <li>• Configure separately for each transaction type</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Periods Tab */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Tab 3</Badge>
                <CardTitle className="text-base">Periods</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Configure depreciation period patterns and financial year structures.
              </p>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold mb-2">Period Patterns</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>4-4-5 Pattern:</strong> Retail calendar with 4-week, 4-week, 5-week quarters</li>
                    <li>• <strong>Monthly:</strong> Standard 12-month calendar year</li>
                    <li>• <strong>Quarterly:</strong> Four equal quarters per year</li>
                    <li>• <strong>Custom:</strong> Define your own period ratios</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Period Configuration</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• View and edit period ratios for each pattern</li>
                    <li>• Ensure period ratios sum to 12 for annual calculations</li>
                    <li>• Preview how depreciation will be distributed across periods</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Integrations Tab */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Tab 4</Badge>
                <CardTitle className="text-base">Integrations</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Connect to external financial systems for seamless data synchronization.
              </p>
              
              <Alert className="border-indigo-200 bg-indigo-50">
                <AlertCircle className="h-4 w-4 text-indigo-600" />
                <AlertTitle>Single Integration</AlertTitle>
                <AlertDescription>
                  Only one financial system integration can be active at a time. Switching integrations will disconnect the current system.
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold mb-2">Available Integrations</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>Access Financials:</strong> Full integration with nominal accounts and cost centers</li>
                    <li>• <strong>Access Dimensions:</strong> Multi-dimensional financial reporting (Coming Soon)</li>
                    <li>• <strong>Xero:</strong> Cloud accounting integration (Coming Soon)</li>
                    <li>• <strong>QuickBooks:</strong> Small business accounting integration (Coming Soon)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Integration Setup</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>1. Select your financial system from the available integrations</li>
                    <li>2. Choose the database instance to connect to</li>
                    <li>3. Verify connection status (Active, Inactive, or Error)</li>
                    <li>4. Save configuration to enable data synchronization</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Data Synchronization</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Nominal accounts are automatically synced from the financial system</li>
                    <li>• Cost centers are pulled from the connected database</li>
                    <li>• Transaction postings can be sent back to the financial system</li>
                    <li>• Last sync timestamp is displayed for each integration</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Language Tab */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Tab 5</Badge>
                <CardTitle className="text-base">Language</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Customize field labels and terminology to match your organization's language preferences.
              </p>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold mb-2">Customizable Labels</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Modify labels for asset fields, transaction types, and system terms</li>
                    <li>• Support for multiple languages and regional terminology</li>
                    <li>• Changes apply system-wide across all screens and reports</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Common Customizations</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Rename "Asset" to "Equipment", "Property", or other terms</li>
                    <li>• Customize depreciation method names</li>
                    <li>• Adjust transaction type labels (Additions, Disposals, etc.)</li>
                    <li>• Modify status and category terminology</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Workflow Section */}
      <div id="workflow">
        <h3 className="text-lg font-semibold mb-4">Configuration Workflow</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Follow this recommended workflow when configuring Asset Options for the first time or making significant changes.
        </p>

        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Badge className="bg-indigo-600 text-white">Step 1</Badge>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Configure General Settings</h4>
              <p className="text-sm text-muted-foreground">
                Start with the Details tab. Set up asset numbering, validation rules, and default depreciation parameters. These settings form the foundation for all asset management activities.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Badge className="bg-indigo-600 text-white">Step 2</Badge>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Set Up Financial Integration</h4>
              <p className="text-sm text-muted-foreground">
                Navigate to the Integrations tab and connect to your financial system. This enables automatic synchronization of nominal accounts and cost centers, ensuring consistency between systems.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Badge className="bg-indigo-600 text-white">Step 3</Badge>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Configure Transaction Settings</h4>
              <p className="text-sm text-muted-foreground">
                In the Transactions tab, assign nominal accounts and cost centers for each transaction type. Configure posting rules and costing options based on your accounting requirements.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Badge className="bg-indigo-600 text-white">Step 4</Badge>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Define Period Structure</h4>
              <p className="text-sm text-muted-foreground">
                Use the Periods tab to select your depreciation period pattern (4-4-5, monthly, etc.). This determines how depreciation is calculated and distributed throughout the year.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Badge className="bg-indigo-600 text-white">Step 5</Badge>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Customize Language (Optional)</h4>
              <p className="text-sm text-muted-foreground">
                If needed, customize field labels in the Language tab to match your organization's terminology. This is particularly useful for international organizations or those with specific naming conventions.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Badge className="bg-indigo-600 text-white">Step 6</Badge>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Review and Save</h4>
              <p className="text-sm text-muted-foreground">
                Review all changes in the confirmation dialog that appears when you click Save. The dialog shows a detailed summary of all modifications organized by tab and section. Confirm to apply changes system-wide.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Key Features Section */}
      <div id="key-features">
        <h3 className="text-lg font-semibold mb-4">Key Features</h3>
        
        <div className="space-y-6">
          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Change Tracking & Confirmation</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Asset Options includes intelligent change tracking that monitors all modifications across all tabs. Before saving, you'll see a detailed confirmation dialog showing:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• All changed fields organized by tab and section</li>
                <li>• Old values vs. new values for each change</li>
                <li>• Clear visual indication of what will be updated</li>
                <li>• Option to review and cancel if needed</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Validation & Error Prevention</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Comprehensive validation ensures data integrity:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• Required fields are validated before saving</li>
                <li>• Percentage values must be between 0-100</li>
                <li>• Period years must be non-negative</li>
                <li>• Integration requires database instance selection</li>
                <li>• Clear error messages guide you to fix issues</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Unsaved Changes Protection</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                The system protects you from accidentally losing work:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• Browser warning when leaving page with unsaved changes</li>
                <li>• Cancel button shows confirmation dialog if changes exist</li>
                <li>• Visual indicator (Save/Cancel buttons) when changes are pending</li>
                <li>• Option to discard changes and revert to saved state</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Dual Storage System</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Settings are saved to both local storage and the backend database:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• Local storage provides immediate access and offline capability</li>
                <li>• Backend database ensures persistence across devices</li>
                <li>• Automatic fallback if backend save fails</li>
                <li>• Settings sync when switching between devices</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Right Panel - On This Page */}
      <div className="fixed right-8 top-64 hidden xl:block w-64">
        <div className="rounded-lg border bg-card p-4">
          <h4 className="text-sm font-semibold mb-3">On This Page</h4>
          <nav className="space-y-2">
            <button
              onClick={() => scrollToSection('overview')}
              className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Overview
            </button>
            <button
              onClick={() => scrollToSection('configuration-tabs')}
              className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Configuration Tabs
            </button>
            <button
              onClick={() => scrollToSection('workflow')}
              className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Workflow
            </button>
            <button
              onClick={() => scrollToSection('key-features')}
              className="block w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Key Features
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

function SettingsContent({ onNavigateToSubSection }: SettingsContentProps) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Settings</h2>
        <p className="text-muted-foreground">
          Configure system features, asset options, integrations, and period management.
        </p>
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">Configuration Areas</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Click on any configuration area below to view detailed documentation and guides.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card 
          className="relative overflow-hidden border-2 border-indigo-200 dark:border-indigo-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('features')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-base">Features</CardTitle>
            </div>
            <CardDescription>Control application features and modules</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Enable or disable features, switch between demo and live modes for each module.
            </p>
            <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-indigo-200 dark:border-indigo-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('asset-options')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-base">Asset Options</CardTitle>
            </div>
            <CardDescription>Configure asset settings and integrations</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Configure general settings, transactions, periods, integrations, and language preferences.
            </p>
            <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-indigo-200 dark:border-indigo-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('integration-log')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-base">Integration Log</CardTitle>
            </div>
            <CardDescription>Monitor system integrations and sync history</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              View integration logs, sync history, and troubleshoot connection issues.
            </p>
            <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-indigo-200 dark:border-indigo-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('period-management')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-base">Period Management</CardTitle>
            </div>
            <CardDescription>Manage financial periods and depreciation cycles</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Control current period, run period-end processes, and sync with Access Financials.
            </p>
            <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-indigo-200 dark:border-indigo-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('teams-permissions')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-base">Teams & Permissions</CardTitle>
            </div>
            <CardDescription>Manage user access and permissions</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Configure team roles, user permissions, and access control settings.
            </p>
            <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Administration Content Component
interface AdministrationContentProps {
  onNavigateToSubSection?: (subSectionId: string) => void;
}

function AdministrationContent({ onNavigateToSubSection }: AdministrationContentProps) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Administration</h2>
        <p className="text-muted-foreground">
          Manage categories, locations, equipment, disposal reasons, and data imports.
        </p>
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">Administration Areas</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Click on any area below to view detailed documentation and guides.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card 
          className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('locations')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-base">Locations</CardTitle>
            </div>
            <CardDescription>Manage physical asset locations</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Define location hierarchies with sub-locations and track asset movements.
            </p>
            <div className="flex items-center gap-2 text-sm text-purple-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('categories')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-base">Categories</CardTitle>
            </div>
            <CardDescription>Organize assets with hierarchical categories</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Create and manage asset categories with default depreciation settings and sub-categories.
            </p>
            <div className="flex items-center gap-2 text-sm text-purple-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('equipment')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-base">Equipment</CardTitle>
            </div>
            <CardDescription>Manage equipment types and specifications</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Define equipment types, specifications, and maintenance requirements.
            </p>
            <div className="flex items-center gap-2 text-sm text-purple-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('disposal-reasons')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-base">Disposal Reasons</CardTitle>
            </div>
            <CardDescription>Define reasons for asset disposal</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Create and manage disposal reason codes for asset write-offs and disposals.
            </p>
            <div className="flex items-center gap-2 text-sm text-purple-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800 cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          onClick={() => onNavigateToSubSection?.('imports')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <div className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-base">Imports</CardTitle>
            </div>
            <CardDescription>Import assets from external sources</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <p className="text-sm text-muted-foreground mb-4">
              Import asset data from CSV files, Excel spreadsheets, and other systems.
            </p>
            <div className="flex items-center gap-2 text-sm text-purple-600 font-medium">
              View detailed guide
              <ArrowRight className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Categories Content Component
function CategoriesContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2 text-indigo-600">Categories</h2>
        <p className="text-muted-foreground">
          Organize your assets with hierarchical categories and default depreciation settings.
        </p>
      </div>

      <Separator />

      {/* Overview Section */}
      <div id="overview" className="space-y-4">
        <h3 className="text-xl font-semibold">Overview</h3>
        <p className="text-muted-foreground">
          Categories help organize your assets into logical groups and can include default depreciation settings that are automatically applied to new assets. Each category can have multiple sub-categories for more granular organization.
        </p>

        <div className="grid gap-4 md:grid-cols-2 mt-6">
          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Hierarchical Structure</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Create parent categories with unlimited sub-categories for detailed asset classification.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Default Depreciation</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Set default depreciation methods, rates, and useful life that apply to all assets in the category.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Asset Tracking</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                View asset counts for each category and sub-category, with quick navigation to asset lists.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Detailed Notes</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Add descriptions and notes to categories for clear documentation and guidance.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Page Layout Section */}
      <div id="page-layout" className="space-y-4">
        <h3 className="text-xl font-semibold">Page Layout & Navigation</h3>
        <p className="text-muted-foreground">
          The Categories page displays all your asset categories in a hierarchical table view with expandable sub-categories.
        </p>

        <div className="space-y-4 mt-4">
          <div className="rounded-lg border p-4 bg-muted/50">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <LayoutList className="h-4 w-4" />
              Main Table View
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Category Name:</strong> Main category identifier with expand/collapse icon for sub-categories</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Code:</strong> Unique category code for reference and reporting</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Asset Count:</strong> Number of assets in the category (clickable to view assets)</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Depreciation Method:</strong> Default depreciation method for the category</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Actions:</strong> Edit or delete category options</span>
              </li>
            </ul>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Search & Filter</AlertTitle>
            <AlertDescription>
              Use the search bar at the top to quickly find categories by name or code. The search works across both parent categories and sub-categories.
            </AlertDescription>
          </Alert>
        </div>
      </div>

      <Separator />

      {/* Workflow Section */}
      <div id="workflow" className="space-y-4">
        <h3 className="text-xl font-semibold">Category Management Workflow</h3>
        <p className="text-muted-foreground">
          Follow these steps to create and manage asset categories effectively.
        </p>

        <div className="space-y-4 mt-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                1
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Create Parent Category</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Click the "Add Category" button and enter the category details including name, code, and optional depreciation defaults.
              </p>
              <div className="rounded-lg border p-3 bg-muted/30 text-sm">
                <strong>Key Fields:</strong> Category Name, Code, Description, Depreciation Method, Rate, Useful Life
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                2
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Add Sub-Categories (Optional)</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Expand a parent category and click "Add Sub-Category" to create more specific classifications.
              </p>
              <Badge variant="outline" className="mt-2">Sub-categories inherit parent settings by default</Badge>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                3
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Configure Depreciation Defaults</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Set default depreciation settings that will be automatically applied to new assets in this category.
              </p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Badge>Straight Line</Badge>
                <Badge>Declining Balance</Badge>
                <Badge>Sum of Years Digits</Badge>
                <Badge>Units of Production</Badge>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                4
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Assign Assets</h4>
              <p className="text-sm text-muted-foreground">
                When creating or editing assets, select the appropriate category. The depreciation defaults will be automatically applied.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Navigation Panel */}
      <div className="hidden xl:block fixed right-8 top-64 w-64">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-1">
              <button
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Overview
              </button>
              <button
                onClick={() => scrollToSection('page-layout')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Page Layout
              </button>
              <button
                onClick={() => scrollToSection('workflow')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Workflow
              </button>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Locations Content Component
function LocationsContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2 text-indigo-600">Locations</h2>
        <p className="text-muted-foreground">
          Manage physical locations where assets are stored or used with hierarchical organization.
        </p>
      </div>

      <Separator />

      {/* Overview Section */}
      <div id="overview" className="space-y-4">
        <h3 className="text-xl font-semibold">Overview</h3>
        <p className="text-muted-foreground">
          Locations represent physical places where assets are stored, used, or maintained. You can create a hierarchy of locations with parent locations and sub-locations for detailed tracking.
        </p>

        <div className="grid gap-4 md:grid-cols-2 mt-6">
          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Location Hierarchy</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Create parent locations (buildings, sites) with sub-locations (rooms, floors) for precise asset tracking.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Address Information</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Store complete address details including street, city, state, and postal code for each location.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Asset Tracking</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                View asset counts for each location and sub-location with quick navigation to asset lists.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <MapPinned className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Movement History</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Track asset movements between locations with complete audit trail and history.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Page Layout Section */}
      <div id="page-layout" className="space-y-4">
        <h3 className="text-xl font-semibold">Page Layout & Navigation</h3>
        <p className="text-muted-foreground">
          The Locations page displays all your locations in a hierarchical table view with expandable sub-locations.
        </p>

        <div className="space-y-4 mt-4">
          <div className="rounded-lg border p-4 bg-muted/50">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <LayoutList className="h-4 w-4" />
              Main Table View
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Location Name:</strong> Location identifier with expand/collapse icon for sub-locations</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Address:</strong> Full address details for the location</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Asset Count:</strong> Number of assets at the location (clickable to view assets)</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Status:</strong> Active or inactive status indicator</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Actions:</strong> Edit or delete location options</span>
              </li>
            </ul>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Search & Filter</AlertTitle>
            <AlertDescription>
              Use the search bar to quickly find locations by name or address. The search works across both parent locations and sub-locations.
            </AlertDescription>
          </Alert>
        </div>
      </div>

      <Separator />

      {/* Workflow Section */}
      <div id="workflow" className="space-y-4">
        <h3 className="text-xl font-semibold">Location Management Workflow</h3>
        <p className="text-muted-foreground">
          Follow these steps to create and manage asset locations effectively.
        </p>

        <div className="space-y-4 mt-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                1
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Create Parent Location</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Click "Add Location" and enter the location details including name, address, and contact information.
              </p>
              <div className="rounded-lg border p-3 bg-muted/30 text-sm">
                <strong>Key Fields:</strong> Location Name, Address, City, State, Postal Code, Country
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                2
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Add Sub-Locations (Optional)</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Expand a parent location and click "Add Sub-Location" to create specific areas within the location.
              </p>
              <Badge variant="outline" className="mt-2">Examples: Rooms, Floors, Departments, Storage Areas</Badge>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                3
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Assign Assets</h4>
              <p className="text-sm text-muted-foreground">
                When creating or editing assets, select the appropriate location and sub-location for accurate tracking.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                4
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Track Movements</h4>
              <p className="text-sm text-muted-foreground">
                Use the asset detail page to move assets between locations. All movements are tracked in the audit history.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Navigation Panel */}
      <div className="hidden xl:block fixed right-8 top-64 w-64">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-1">
              <button
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Overview
              </button>
              <button
                onClick={() => scrollToSection('page-layout')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Page Layout
              </button>
              <button
                onClick={() => scrollToSection('workflow')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Workflow
              </button>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Features Content Component  
function FeaturesContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2 text-indigo-600">Features</h2>
        <p className="text-muted-foreground">
          Control which features are enabled and configure demo vs. live mode for each module.
        </p>
      </div>

      <Separator />

      {/* Overview Section */}
      <div id="overview" className="space-y-4">
        <h3 className="text-xl font-semibold">Overview</h3>
        <p className="text-muted-foreground">
          The Features page allows you to enable or disable specific modules and switch between demo mode (using mock data) and live mode (using real API data) for each feature independently.
        </p>

        <div className="grid gap-4 md:grid-cols-2 mt-6">
          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Feature Toggle</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Enable or disable entire modules to customize the application to your needs.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Demo vs. Live Mode</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Switch between demo data (for testing) and live API data (for production) per module.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Bulk Actions</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Quickly enable all features, switch all to demo, or switch all to live mode with one click.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Save className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Persistent Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Feature settings are saved and persist across sessions and browser refreshes.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Feature Categories Section */}
      <div id="categories" className="space-y-4">
        <h3 className="text-xl font-semibold">Feature Categories</h3>
        <p className="text-muted-foreground">
          Features are organized into logical categories for easy management.
        </p>

        <div className="space-y-4 mt-4">
          <div className="rounded-lg border p-4 bg-muted/50">
            <h4 className="font-semibold mb-3">Core Operations</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Badge variant="outline">Assets</Badge>
                <span>Asset management, viewing, and editing</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge variant="outline">Dashboard</Badge>
                <span>Financial and maintenance dashboards</span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border p-4 bg-muted/50">
            <h4 className="font-semibold mb-3">Transaction Management</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Badge variant="outline">Depreciation</Badge>
                <span>Depreciation calculation and posting</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge variant="outline">Additions</Badge>
                <span>Asset additions and capitalizations</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge variant="outline">Revaluation</Badge>
                <span>Asset revaluation processing</span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border p-4 bg-muted/50">
            <h4 className="font-semibold mb-3">Operational Tools</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Badge variant="outline">Maintenance</Badge>
                <span>Maintenance scheduling and tracking</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge variant="outline">Check-In/Out</Badge>
                <span>Asset check-in and check-out management</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge variant="outline">Audits</Badge>
                <span>Physical asset audits and verification</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Separator />

      {/* Mode Configuration Section */}
      <div id="modes" className="space-y-4">
        <h3 className="text-xl font-semibold">Understanding Modes</h3>
        <p className="text-muted-foreground">
          Each feature can operate in one of three modes depending on your needs.
        </p>

        <div className="grid gap-4 mt-4">
          <Card className="border-green-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <CardTitle className="text-base">Live Mode</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                Uses real API data from your backend. Recommended for production use.
              </p>
              <Badge variant="outline" className="bg-green-50">Production Ready</Badge>
            </CardContent>
          </Card>

          <Card className="border-blue-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-base">Demo Mode</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                Uses mock data for testing and demonstration. No real data is affected.
              </p>
              <Badge variant="outline" className="bg-blue-50">Testing & Training</Badge>
            </CardContent>
          </Card>

          <Card className="border-gray-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-gray-600" />
                <CardTitle className="text-base">Disabled</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                Feature is completely disabled and hidden from the navigation menu.
              </p>
              <Badge variant="outline" className="bg-gray-50">Not Available</Badge>
            </CardContent>
          </Card>
        </div>

        <Alert className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Important Note</AlertTitle>
          <AlertDescription>
            Changes to feature settings take effect immediately. Users may need to refresh their browser to see navigation changes.
          </AlertDescription>
        </Alert>
      </div>

      {/* Sticky Navigation Panel */}
      <div className="hidden xl:block fixed right-8 top-64 w-64">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-1">
              <button
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Overview
              </button>
              <button
                onClick={() => scrollToSection('categories')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Feature Categories
              </button>
              <button
                onClick={() => scrollToSection('modes')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Understanding Modes
              </button>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Period Management Content Component
function PeriodManagementContent() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2 text-indigo-600">Period Management</h2>
        <p className="text-muted-foreground">
          Manage financial periods, run period-end processes, and sync with Access Financials.
        </p>
      </div>

      <Separator />

      {/* Overview Section */}
      <div id="overview" className="space-y-4">
        <h3 className="text-xl font-semibold">Overview</h3>
        <p className="text-muted-foreground">
          Period Management controls your current financial period and enables period-end processing including depreciation calculations. It also provides integration with Access Financials for period synchronization.
        </p>

        <div className="grid gap-4 md:grid-cols-2 mt-6">
          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Current Period</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                View and update the current financial year and period for depreciation processing.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Play className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Period-End Process</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Run period-end to calculate depreciation and advance to the next period automatically.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Financials Sync</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Synchronize periods with Access Financials to ensure alignment across systems.
              </p>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">Auto-Sync</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Enable automatic period synchronization with Access Financials on a schedule.
              </p>
            </CardContent>
          </Card>
        </div>

        <Alert className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Integration Required</AlertTitle>
          <AlertDescription>
            Period synchronization features require an active Access Financials integration. Configure this in Settings &gt; Integrations.
          </AlertDescription>
        </Alert>
      </div>

      <Separator />

      {/* Current Period Section */}
      <div id="current-period" className="space-y-4">
        <h3 className="text-xl font-semibold">Current Period Settings</h3>
        <p className="text-muted-foreground">
          The current period determines which financial period is active for depreciation calculations and transaction posting.
        </p>

        <div className="space-y-4 mt-4">
          <div className="rounded-lg border p-4 bg-muted/50">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Period Information
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Current Year:</strong> The financial year currently active (e.g., 2024)</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Current Period:</strong> The period number within the year (1-12 for monthly periods)</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                <span><strong>Last Updated:</strong> Timestamp of the last period change</span>
              </li>
            </ul>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Manual Period Updates</AlertTitle>
            <AlertDescription>
              You can manually update the current period if needed. However, it's recommended to use the Period-End process to ensure all depreciation is calculated correctly.
            </AlertDescription>
          </Alert>
        </div>
      </div>

      <Separator />

      {/* Period-End Process Section */}
      <div id="period-end" className="space-y-4">
        <h3 className="text-xl font-semibold">Period-End Process</h3>
        <p className="text-muted-foreground">
          The period-end process calculates depreciation for all assets and advances the current period.
        </p>

        <div className="space-y-4 mt-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                1
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Review Current Period</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Verify that all transactions for the current period are complete before running period-end.
              </p>
              <Badge variant="outline">Check depreciation batches, additions, and revaluations</Badge>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                2
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Run Period-End</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Click "Run Period-End" and choose whether to calculate depreciation automatically.
              </p>
              <div className="rounded-lg border p-3 bg-muted/30 text-sm mt-2">
                <strong>Options:</strong> Run Depreciation (recommended), Skip Depreciation (manual calculation)
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                3
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Review Results</h4>
              <p className="text-sm text-muted-foreground">
                After completion, review the period-end summary showing assets processed, depreciation calculated, and any errors.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
                4
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">Period Advanced</h4>
              <p className="text-sm text-muted-foreground">
                The current period is automatically advanced to the next period. You can now process transactions for the new period.
              </p>
            </div>
          </div>
        </div>

        <Alert className="mt-6" variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription>
            Period-end is a critical process. Ensure you have a backup and that all users have completed their work for the current period before proceeding.
          </AlertDescription>
        </Alert>
      </div>

      {/* Sticky Navigation Panel */}
      <div className="hidden xl:block fixed right-8 top-64 w-64">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-1">
              <button
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Overview
              </button>
              <button
                onClick={() => scrollToSection('current-period')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Current Period
              </button>
              <button
                onClick={() => scrollToSection('period-end')}
                className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
              >
                Period-End Process
              </button>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function GlossaryContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(glossaryTerms.map(t => t.category)));

  const filteredTerms = glossaryTerms.filter(term => {
    const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Glossary</h2>
        <p className="text-muted-foreground">
          Key terms and definitions used throughout Access Assets.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search terms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(null)}
          >
            All
          </Button>
          {categories.map(category => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        {filteredTerms.map(term => (
          <Card key={term.term} className="relative overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{term.term}</CardTitle>
                <Badge variant="outline" className="shrink-0">{term.category}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{term.definition}</p>
            </CardContent>
          </Card>
        ))}
        {filteredTerms.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No terms found matching your search.
          </div>
        )}
      </div>
    </div>
  );
}

function CalculationsContent() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Depreciation Calculations</h2>
        <p className="text-muted-foreground">
          Detailed explanations of depreciation methods with formulas and examples.
        </p>
      </div>

      <Card className="relative overflow-hidden border-2 border-red-200 dark:border-red-800">
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-red-600" />
            Understanding Depreciation
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            Depreciation is the systematic allocation of an asset's cost over its useful life. Different methods produce different expense patterns, which can affect financial statements and tax obligations.
          </p>
          <div className="rounded-lg border bg-background/50 p-4">
            <h4 className="font-semibold text-sm mb-2">Key Variables</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li><strong>Cost:</strong> Original acquisition cost of the asset</li>
              <li><strong>Residual Value:</strong> Estimated value at end of useful life (also called salvage value)</li>
              <li><strong>Useful Life:</strong> Expected period the asset will be used</li>
              <li><strong>Depreciable Amount:</strong> Cost minus Residual Value</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {calculationMethods.map(method => (
          <Card key={method.id} className="relative overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">{method.name}</CardTitle>
              <CardDescription>{method.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-muted/50 p-4">
                <h4 className="font-semibold text-sm mb-2">Formula</h4>
                <code className="text-sm bg-background px-2 py-1 rounded">{method.formula}</code>
              </div>

              <div className="rounded-lg border p-4">
                <h4 className="font-semibold text-sm mb-3">Example Calculation</h4>
                <div className="grid gap-2 md:grid-cols-3 mb-4">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Asset Cost:</span>
                    <span className="ml-2 font-medium">£{method.example.assetCost.toLocaleString()}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Residual Value:</span>
                    <span className="ml-2 font-medium">£{method.example.salvageValue.toLocaleString()}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Useful Life:</span>
                    <span className="ml-2 font-medium">{method.example.usefulLife} years</span>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="space-y-1">
                  {method.example.calculations.map((calc) => (
                    <p key={calc} className={cn('text-sm font-mono', calc === '' ? 'h-2' : '')}>
                      {calc}
                    </p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Choosing the Right Method</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Straight-Line</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Assets that provide consistent value over time (buildings, furniture). Simple to calculate and understand.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Declining Balance</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Assets that lose value quickly in early years (vehicles, technology). Matches expense to benefit received.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Sum of Years Digits</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Similar to declining balance but with a more predictable pattern. Good for assets with higher early productivity.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Units of Production</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Manufacturing equipment, vehicles. Depreciation directly tied to actual usage rather than time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function HelpPage(): React.ReactElement {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [activeSubSection, setActiveSubSection] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    setActiveSubSection(undefined);
  };

  const handleSubSectionClick = (subSectionId: string) => {
    setActiveSubSection(subSectionId);
  };

  const renderContent = () => {
    // Handle sub-sections first
    if (activeSection === 'transactions' && activeSubSection) {
      switch (activeSubSection) {
        case 'depreciation':
          return <DepreciationContent />;
        case 'additions':
          return <AdditionsContent />;
        case 'revaluation':
          return <RevaluationContent />;
        default:
          return <TransactionsContent onNavigateToSubSection={handleSubSectionClick} />;
      }
    }

    if (activeSection === 'audits' && activeSubSection) {
      switch (activeSubSection) {
        case 'create-audit':
          return <CreateAuditContent />;
        case 'active-audits':
          return <ActiveAuditsContent />;
        case 'scheduled-audits':
          return <ScheduledAuditsContent />;
        case 'audit-history':
          return <AuditHistoryContent />;
        case 'audit-exceptions':
          return <AuditExceptionsContent />;
        default:
          return <AuditsContent onNavigateToSubSection={handleSubSectionClick} />;
      }
    }

    if (activeSection === 'administration' && activeSubSection) {
      switch (activeSubSection) {
        case 'teams-permissions':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Teams & Permissions</h2><p className="text-muted-foreground">Documentation for Teams & Permissions coming soon.</p></div></div>;
        case 'locations':
          return <LocationsContent />;
        case 'categories':
          return <CategoriesContent />;
        case 'equipment':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Equipment</h2><p className="text-muted-foreground">Documentation for Equipment management coming soon.</p></div></div>;
        case 'disposal-reasons':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Disposal Reasons</h2><p className="text-muted-foreground">Documentation for Disposal Reasons coming soon.</p></div></div>;
        case 'imports':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Imports</h2><p className="text-muted-foreground">Documentation for Imports coming soon.</p></div></div>;
        default:
          return <AdministrationContent onNavigateToSubSection={handleSubSectionClick} />;
      }
    }

    if (activeSection === 'settings' && activeSubSection) {
      switch (activeSubSection) {
        case 'features':
          return <FeaturesContent />;
        case 'asset-options':
          return <AssetOptionsContent />;
        case 'integration-log':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Integration Log</h2><p className="text-muted-foreground">Documentation for Integration Log coming soon.</p></div></div>;
        case 'period-management':
          return <PeriodManagementContent />;
        case 'teams-permissions':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Teams & Permissions</h2><p className="text-muted-foreground">Documentation for Teams & Permissions coming soon.</p></div></div>;
        default:
          return <SettingsContent onNavigateToSubSection={handleSubSectionClick} />;
      }
    }

    if (activeSection === 'administration' && activeSubSection) {
      switch (activeSubSection) {
        case 'categories':
          return <CategoriesContent />;
        case 'locations':
          return <LocationsContent />;
        case 'equipment':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Equipment</h2><p className="text-muted-foreground">Documentation for Equipment coming soon.</p></div></div>;
        case 'disposal-reasons':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Disposal Reasons</h2><p className="text-muted-foreground">Documentation for Disposal Reasons coming soon.</p></div></div>;
        case 'imports':
          return <div className="space-y-8 max-w-5xl"><div><h2 className="text-2xl font-bold tracking-tight mb-2">Imports</h2><p className="text-muted-foreground">Documentation for Imports coming soon.</p></div></div>;
        default:
          return <AdministrationContent onNavigateToSubSection={handleSubSectionClick} />;
      }
    }

    // Handle main sections
    switch (activeSection) {
      case 'dashboard':
        return <DashboardContent />;
      case 'assets':
        return <AssetsContent />;
      case 'transactions':
        return <TransactionsContent onNavigateToSubSection={handleSubSectionClick} />;
      case 'audits':
        return <AuditsContent onNavigateToSubSection={handleSubSectionClick} />;
      case 'administration':
        return <AdministrationContent onNavigateToSubSection={handleSubSectionClick} />;
      case 'settings':
        return <SettingsContent onNavigateToSubSection={handleSubSectionClick} />;
      case 'glossary':
        return <GlossaryContent />;
      case 'calculations':
        return <CalculationsContent />;
      default:
        return <DashboardContent />;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-primary" />
              Help & Support
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Comprehensive guides, tutorials, and reference documentation for Access Assets
            </p>
          </div>

        </div>
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="w-72 border-r bg-muted/30 flex flex-col">
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <Separator />
          <div className="flex-1 p-3">
            <div className="space-y-1">
              {docSections.map(section => (
                <SectionNavItem
                  key={section.id}
                  section={section}
                  isActive={activeSection === section.id}
                  onClick={() => handleSectionClick(section.id)}
                  activeSubSection={activeSubSection}
                  onSubSectionClick={handleSubSectionClick}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-8 max-w-4xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
